import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { validateReframePreview } from "@/lib/reframe-geometry";
import { sanitizeFilename } from "@/lib/filename";
import { bandRepairNotice, type BandRepairResult } from "@shared/band-repair";
import { ARCHIVE_RETENTION_HELPER, ARCHIVE_EMPTY_COPY, archiveRetention } from "@shared/archive-retention";
import { downloadSavedVideo, downloadVideoToDisk } from "@/lib/stream-video-download";
import { creativePerformanceAdHref } from "@/hooks/use-creative-performance-ad-link";
import { useParams, Link, useSearch, useLocation } from "wouter";
import {
  Search, Bookmark, Trophy, Archive, ArchiveRestore, Download, ExternalLink,
  Filter, Loader2, ImageOff, Folder, Crop, Pencil, Video, Images,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, LayoutGrid, Laugh,
  CheckSquare, X, MessageSquare, Wand2, History, ImagePlus, RefreshCw,
  Copy as CopyIcon, Eye, EyeOff, Link2, Layers, PackageOpen, PackagePlus, CheckCircle2,
} from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useAdNotesEnabled, useAdNoteCounts, AdNotesDialog, BrandNotesView,
} from "@/components/ad-notes";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogTitle,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/lib/picker-search";
import { SafeZoneOverlay, SafeZoneToggle } from "@/components/safe-zone-overlay";
import { InpaintingModal } from "@/components/campaign-wizard/inpainting-modal";
import { useMyAdsImageEdits, type MyAdsEditState } from "@/hooks/use-my-ads-image-edits";
import { AdImage } from "@/components/ad-image";
import { LibraryImagePreview } from "@/components/library-image-preview";
import { InlineRenameTitle } from "@/components/inline-rename-title";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useAuth } from "@/hooks/use-auth";
import type {
  Persona, Product, Offer, NarrativeAngle, AdUnitType, GeneratedAd, Project,
} from "@shared/schema";
import { type AspectRatio, AD_ASPECT_RATIO_OPTIONS } from "@shared/aspectRatios";
import { LIBRARY_CATEGORIES, type LibraryCategory } from "@shared/library-category";
import { ManualAdUpload } from "@/components/manual-ad-upload";
import { useUndoableAdIds, useRedoableAdIds, invalidateUndoableAds } from "@/components/undo-ad-edit";
import { Undo2, Redo2 } from "lucide-react";
import { ManualAdEditDialog } from "@/components/manual-ad-edit";
import { duplicateAdAsIs, copyAndReframeAd } from "@/lib/ad-copy";
import { creativeIterationSeedHref } from "@/lib/creative-iterations";
import { canOpenPromptImageEditor } from "@shared/prompt-image-edit";
import {
  WeeklyDeliveriesSheet,
  ApprovalBadge,
  useApprovalStates,
  type AdApprovalStatus,
} from "@/components/weekly-deliveries-sheet";

// How many cards a collapsed category section shows before "View More"
// (two rows at the widest 4-column breakpoint), mirroring Ads Express's
// collapsed template buckets.
import { useQuery, useInfiniteQuery, type UseInfiniteQueryResult, type InfiniteData } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateApprovalStates } from "@/lib/approval-states";
const CATEGORY_COLLAPSED_COUNT = 8;

const LIBRARY_PAGE_SIZE = 24;
const BULK_DOWNLOAD_LIMIT = 100;

// Per-category header visuals (icon + accent), Ads Express bucket style.
const CATEGORY_VISUALS: Record<LibraryCategory, { icon: JSX.Element; accent: string; bar: string }> = {
  ads: {
    icon: <LayoutGrid className="h-4 w-4" />,
    accent: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
    bar: "bg-violet-500",
  },
  memes: {
    icon: <Laugh className="h-4 w-4" />,
    accent: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    bar: "bg-amber-500",
  },
  slideshows: {
    icon: <Images className="h-4 w-4" />,
    accent: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
    bar: "bg-sky-500",
  },
  videos: {
    icon: <Video className="h-4 w-4" />,
    accent: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
    bar: "bg-rose-500",
  },
};

type LibraryStatus = "saved" | "winner" | "archived" | "all";

// The shape returned by GET /api/brands/:brandId/generated-ads — base
// row + the enriched fields the route adds.
type LibraryAd = GeneratedAd & {
  durableUrl: string | null;
  // Unified media URL from the listing route (durable video URL for video
  // rows, else the durable/original image URL).
  mediaUrl: string | null;
  personaNameSnapshot: string | null;
  // Originating standard Ads Express selector snapshot. The enabled flag
  // distinguishes an unavailable character from outputs that never used the
  // selector (legacy, KC, and meme), which should not render a badge.
  personaCharacterSelectorEnabled: boolean;
  selectedPersonaCharacterNameSnapshot: string | null;
  productNameSnapshot: string | null;
  angleNameSnapshot: string | null;
  angleFamilySnapshot: string | null;
  projectName: string | null;
  // Display name of the collaborator who created this ad's parent generation
  // (first+last name, falling back to email). Null when unresolved.
  createdByName: string | null;
  // Derived source category (ads | memes | slideshows | videos).
  category: LibraryCategory;
  eligibleIterationSource: boolean;
  sourcePerformanceAd?: { adId: string; adName: string } | null;
};

// Creator filter value: "everyone" | "mine" | "user:<id>" (a specific
// collaborator picked by name).
type CreatorFilter = string;

type LibraryCreator = { id: string; name: string };

type CategoryQuery = UseInfiniteQueryResult<InfiniteData<LibraryPage>, Error>;
const STATUS_OPTIONS: Array<{ value: LibraryStatus; label: string }> = [
  { value: "saved", label: "Saved + Winners" },
  { value: "winner", label: "Winners only" },
  { value: "all", label: "All saved (incl. drafts)" },
  { value: "archived", label: "Archived" },
];

function useStableCallback<A extends unknown[], R>(fn: (...args: A) => R): (...args: A) => R {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args: A) => ref.current(...args), []);
}
export default function AdLibraryPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();

  // Filter state — kept as plain primitives so the URL string stays stable.
  const [status, setStatus] = useState<LibraryStatus>("saved");
  const [personaId, setPersonaId] = useState<string>("all");
  const [productId, setProductId] = useState<string>("all");
  const [offerId, setOfferId] = useState<string>("all");
  const [angleId, setAngleId] = useState<string>("all");
  const [adUnitId, setAdUnitId] = useState<string>("all");
  const [projectId, setProjectId] = useState<string>("all");
  const [creator, setCreator] = useState<CreatorFilter>("everyone");
  // Task #1736 — per-category visible-card counts. Sections start collapsed
  // at CATEGORY_COLLAPSED_COUNT; each "View More" click grows the slice by
  // VIEW_MORE_BATCH (fetching more pages from the server when needed) instead
  // of expanding the whole category at once. Reset whenever the filtered
  // listing changes.
  const [visibleCounts, setVisibleCounts] = useState<Partial<Record<LibraryCategory, number>>>({});
  // Category currently fetching its next page from a "View More" click.
  const [loadingMoreCategory, setLoadingMoreCategory] = useState<LibraryCategory | null>(null);
  const [tag, setTag] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  // Task #1735 — debounce the free-text filters so typing doesn't change the
  // React Query key (and refetch the listing) on every keystroke. The raw
  // state still drives the inputs + "filters active" indicator; only the
  // settled values feed the listing URL. Clearing to empty applies
  // immediately so "clear filters" resets the list without the delay.
  const [debouncedTag, setDebouncedTag] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  useEffect(() => {
    if (!search.trim()) {
      setDebouncedSearch("");
      return;
    }
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => {
    if (!tag.trim()) {
      setDebouncedTag("");
      return;
    }
    const t = setTimeout(() => setDebouncedTag(tag), 300);
    return () => clearTimeout(t);
  }, [tag]);
  // 9:16 → 4:5 safe-zone preview overlay (Task #819). Applies to any card
  // whose (client-side detected) ratio is 9:16.
  const [showSafeZone, setShowSafeZone] = useState(false);

  // Task #1625 — ad notes (user-scoped `ad_notes` beta grant). When enabled
  // for the account, cards show a note-count indicator + notes dialog, and a
  // brand-wide Notes view
  // becomes available from the header.
  const notesEnabled = useAdNotesEnabled(brandId);
  const noteCounts = useAdNoteCounts(brandId, notesEnabled);

  // Task #1689 — "Fix bands" repair for 9:16 ads, shown only when the
  // brand's `safe_zone_enhanced_render` flag is active (the server 403s
  // fail-closed regardless). Per-ad in-progress set: repairing one ad never
  // locks the rest of the grid.
  const { data: betaFlags } = useQuery<Record<string, boolean>>({
    queryKey: [`/api/brands/${brandId}/beta-features`],
    enabled: !!brandId,
    staleTime: 60_000,
  });
  const bandRepairEnabled = !!betaFlags?.safe_zone_enhanced_render;
  // Task #2388 — "Edit Creative" entry point (user-scoped creative_editor
  // beta grant; the server 403s fail-closed regardless).
  const creativeEditorEnabled = !!betaFlags?.creative_editor;
  const creativeIterationsVideoEnabled = !!betaFlags?.creative_iterations_video;
  // Weekly Deliverables — user-scoped beta grant, resolved fail-closed.
  // Review entry points are visible only when the requesting account is enabled;
  // server routes separately enforce admin work.
  const weeklyDeliverablesEnabled = !!betaFlags?.weekly_deliverables;
  const [deliveriesSheetOpen, setDeliveriesSheetOpen] = useState(false);
  const [requestedDeliveryProjectId, setRequestedDeliveryProjectId] = useState<string | null>(null);
  const [requestedDeliveryId, setRequestedDeliveryId] = useState<string | null>(null);
  type BulkBandRepairJob = {
    startedAt: number;
    finishedAt: number | null;
    total: number;
    processed: number;
    repaired: number;
    skipped: number;
    failed: number;
    running: boolean;
    lastError: string | null;
  };
  // Task #1858 follow-on — master admins always get band repair (per-ad
  // button + the bulk tool below), even on opted-out brands; the server
  // gates both master-side too.
  const { user: pageAuthUser } = useAuth();
  const pageIsMaster = pageAuthUser?.isMaster === true;
  // Weekly Deliverables — per-ad approval STATE (current vs earlier version).
  // Adding ads to a delivery is project-level curation done inside the sheet,
  // not a per-ad action here.
  const approvalStates = useApprovalStates(
    brandId,
    weeklyDeliverablesEnabled && pageIsMaster,
  );
  // Master-only bulk 9:16 band repair job for this brand. Poll while running.
  const { data: bulkBandStatus } = useQuery<{ job: BulkBandRepairJob | null }>({
    queryKey: [`/api/brands/${brandId}/repair-916-bands/status`],
    enabled: !!brandId && pageIsMaster,
    refetchInterval: (q) => (q.state.data?.job?.running ? 4000 : false),
  });
  const bulkBandJob = bulkBandStatus?.job ?? null;
  const prevBulkRunning = useRef(false);
  useEffect(() => {
    if (prevBulkRunning.current && bulkBandJob && !bulkBandJob.running) {
      toast({
        title: "Bulk band repair finished",
        description: `Repaired ${bulkBandJob.repaired}, skipped ${bulkBandJob.skipped} (bands already fine), failed ${bulkBandJob.failed}.`,
      });
      invalidateApprovalStates(queryClient, brandId);
      invalidate();
    }
    prevBulkRunning.current = !!bulkBandJob?.running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkBandJob?.running]);
  const startBulkBandRepair = async () => {
    if (
      !window.confirm(
        "Scan every saved 9:16 ad in this brand and re-render ONLY the top/bottom bands of the ones with blurry fallback bands? The ad creatives themselves are never touched. This runs in the background and bills one expansion render per repaired ad.",
      )
    ) {
      return;
    }
    try {
      await apiRequest("POST", `/api/brands/${brandId}/repair-916-bands`, {});
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/repair-916-bands/status`] });
      toast({ title: "Bulk band repair started", description: "Repairing blurry 9:16 bands in the background." });
    } catch (e: unknown) {
      toast({
        title: "Couldn't start band repair",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };
  // Manual uploads are default-on per brand, with an explicit admin opt-out.
  // Wait for the effective server flag; absent/error responses stay fail-closed.
  const manualUploadEnabled = betaFlags?.ads_express_manual_upload === true;
  const [uploadOpen, setUploadOpen] = useState(false);
  // Task #1802 — manual-upload row currently open in the edit dialog
  // (retag persona/product/angle, replace the image). Flag-gated.
  const [manualEditAd, setManualEditAd] = useState<LibraryAd | null>(null);
  const [fixingBandIds, setFixingBandIds] = useState<Set<string>>(new Set());
  const fixingBandsRef = useRef(new Set<string>());

  // Task #1669 — ads degraded by the 2026-07-18..28 edit bug that still have
  // a surviving pre-degradation original in quarantine. Only these show the
  // "Restore original" action; every other ad is untouched.
  const { data: restorableData } = useQuery<{ adIds: string[] }>({
    queryKey: [`/api/brands/${brandId}/generated-ads-restorable`],
    enabled: !!brandId,
    staleTime: 60_000,
  });
  const restorableAdIds = useMemo(
    () => new Set(restorableData?.adIds ?? []),
    [restorableData],
  );
  const [restoringIds, setRestoringIds] = useState<Set<string>>(new Set());
  // Task #2040 — ads with an undoable last edit (a surviving superseded
  // version in quarantine). Server-driven; drops out once purged.
  const undoableAdIds = useUndoableAdIds(brandId);
  const [undoingIds, setUndoingIds] = useState<Set<string>>(new Set());
  const undoEdit = async (ad: LibraryAd) => {
    setUndoingIds((prev) => new Set(prev).add(ad.id));
    try {
      const res = await apiRequest("POST", `/api/generated-ads/${ad.id}/undo-edit`, {});
      const restored = (await res.json()) as { durableUrl?: string };
      // If the edit modal is open on this ad, swap its source to the
      // restored version (same pattern as restoreOriginal).
      if (editState.open && editState.adId === ad.id && restored?.durableUrl) {
        setEditState({ open: true, adId: ad.id, imageUrl: restored.durableUrl });
      }
      toast({
        title: "Edit undone",
        description: "The ad was restored to its previous version.",
      });
      invalidateApprovalStates(queryClient, brandId);
      invalidate();
    } catch (e: unknown) {
      toast({
        title: "Couldn't undo the edit",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      // Success or failure, re-derive availability (a 404/410 means the
      // previous version is gone — the button should disappear).
      invalidateUndoableAds(brandId);
      setUndoingIds((prev) => {
        const next = new Set(prev);
        next.delete(ad.id);
        return next;
      });
    }
  };
  // Task #2040 follow-up — "Redo": bring back the edit an undo just removed.
  // Separate from Undo (which only ever goes backwards); available only right
  // after an undo, until a newer edit lands or retention passes.
  const redoableAdIds = useRedoableAdIds(brandId);
  const [redoingIds, setRedoingIds] = useState<Set<string>>(new Set());
  const redoEdit = async (ad: LibraryAd) => {
    setRedoingIds((prev) => new Set(prev).add(ad.id));
    try {
      const res = await apiRequest("POST", `/api/generated-ads/${ad.id}/redo-edit`, {});
      const restored = (await res.json()) as { durableUrl?: string };
      if (editState.open && editState.adId === ad.id && restored?.durableUrl) {
        setEditState({ open: true, adId: ad.id, imageUrl: restored.durableUrl });
      }
      toast({
        title: "Edit redone",
        description: "The undone edit was brought back.",
      });
      invalidateApprovalStates(queryClient, brandId);
      invalidate();
    } catch (e: unknown) {
      toast({
        title: "Couldn't redo the edit",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      invalidateUndoableAds(brandId);
      setRedoingIds((prev) => {
        const next = new Set(prev);
        next.delete(ad.id);
        return next;
      });
    }
  };
  const restoreOriginal = async (ad: LibraryAd) => {
    setRestoringIds((prev) => new Set(prev).add(ad.id));
    try {
      const res = await apiRequest("POST", `/api/generated-ads/${ad.id}/restore-original`, {});
      // Wait for the fresh listing before releasing the button, so the tile
      // AND anything opened from it (e.g. the Edit modal, which snapshots the
      // ad's current image URL) immediately use the restored original.
      const restored = (await res.json()) as { durableUrl?: string };
      if (editState.open && editState.adId === ad.id && restored?.durableUrl) {
        setEditState({ open: true, adId: ad.id, imageUrl: restored.durableUrl });
      }
      toast({
        title: "Original restored",
        description: "The ad's image was reverted to its original.",
      });
      // Task #2040 — the restore quarantined the replaced image, which is
      // now the ad's undoable previous version.
      invalidateUndoableAds(brandId);
      await Promise.all([
        invalidateApprovalStates(queryClient, brandId),
        queryClient.invalidateQueries({
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            typeof q.queryKey[0] === "string" &&
            (q.queryKey[0] as string).startsWith(`/api/brands/${brandId}/generated-ads`),
        }),
      ]);
    } catch (e: unknown) {
      toast({
        title: "Restore failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setRestoringIds((prev) => {
        const next = new Set(prev);
        next.delete(ad.id);
        return next;
      });
    }
  };
  const [view, setView] = useState<"ads" | "notes">("ads");
  const [notesDialogAd, setNotesDialogAd] = useState<{ adId: string; adName: string | null } | null>(null);

  // Task #1702 — "?adId=..." deep-link (from notes) that lands on the ad's
  // card: resets filters (widening status if needed), expands the card's
  // category section, scrolls the card into view and highlights it briefly.
  const searchStr = useSearch();
  const [, setLocation] = useLocation();
  const requestedView = new URLSearchParams(searchStr).get("view");
  // Dashboard links open the existing My Ads Notes screen. The master-only
  // review API is independent of the beta grant; ordinary note actions are not.
  const notesViewAvailable = notesEnabled || pageIsMaster;
  useEffect(() => {
    if (requestedView === "notes" && notesViewAvailable) setView("notes");
    else if (requestedView !== "notes") setView("ads");
  }, [requestedView, notesViewAvailable, brandId]);
  const changeLibraryView = (nextView: "ads" | "notes") => {
    setView(nextView);
    const params = new URLSearchParams(searchStr);
    if (nextView === "notes") {
      params.set("view", "notes");
      params.delete("adId");
    } else params.delete("view");
    const query = params.toString();
    setLocation(`/brands/${brandId}/library${query ? `?${query}` : ""}`);
  };
  const deepLinkAdId = useMemo(
    () => new URLSearchParams(searchStr).get("adId"),
    [searchStr],
  );
  const deepLinkDeliveryId = useMemo(
    () => new URLSearchParams(searchStr).get("deliveryId"),
    [searchStr],
  );
  const [highlightedAdId, setHighlightedAdId] = useState<string | null>(null);
  // Pending deep-link + which status phase we're searching under ("all"
  // first, then "archived"; not found in either = deleted → friendly toast).
  const deepLinkRef = useRef<{ adId: string; phase: LibraryStatus } | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const showDeliveryDeepLinkHighlight = useCallback((adId: string) => {
    setHighlightedAdId(adId);
    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    // Keep the confirmation visible long enough for a person arriving from a
    // crowded scoreboard to orient themselves after the grid expands.
    highlightTimerRef.current = window.setTimeout(() => setHighlightedAdId(null), 10_000);
  }, []);

  useEffect(() => {
    if (!deepLinkDeliveryId || !brandId || betaFlags === undefined) return;
    if (weeklyDeliverablesEnabled && pageIsMaster) {
      setRequestedDeliveryId(deepLinkDeliveryId);
      setDeliveriesSheetOpen(true);
    }
    const params = new URLSearchParams(searchStr);
    params.delete("deliveryId");
    const rest = params.toString();
    setLocation(`/brands/${brandId}/library${rest ? `?${rest}` : ""}`, { replace: true });
  }, [
    deepLinkDeliveryId,
    brandId,
    betaFlags,
    weeklyDeliverablesEnabled,
    pageIsMaster,
    searchStr,
    setLocation,
  ]);

  // "+ New project" dialog state. When opened from a card's project picker,
  // pendingMoveAdIdRef remembers which ad should be moved into the freshly-
  // created project once the user clicks Create. We use a ref (not state) so
  // the value survives any re-renders that may occur between opening the
  // dialog and the user clicking Create — including Radix Dialog mount
  // lifecycle events that previously could clear it through onOpenChange.
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  // Task #1948 — master-only project management (list + rename/delete).
  const [manageProjectsOpen, setManageProjectsOpen] = useState(false);
  const [renameProjectTarget, setRenameProjectTarget] = useState<Project | null>(null);
  const [renameProjectName, setRenameProjectName] = useState("");
  const [deleteProjectTarget, setDeleteProjectTarget] = useState<Project | null>(null);
  const [projectActionBusy, setProjectActionBusy] = useState(false);
  // Task #2185 — master-only visibility toggle + project deep-link copying.
  const toggleProjectVisibility = async (p: Project) => {
    const next = p.visibility === "master_only" ? "everyone" : "master_only";
    setProjectActionBusy(true);
    try {
      await apiRequest("PATCH", `/api/projects/${p.id}`, { visibility: next });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/projects`] });
      toast({
        title: next === "master_only" ? "Project hidden" : "Project visible",
        description:
          next === "master_only"
            ? `"${p.name}" is now visible to master admins only.`
            : `"${p.name}" is now visible to everyone with brand access.`,
      });
    } catch (e: unknown) {
      toast({
        title: "Couldn't update visibility",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setProjectActionBusy(false);
    }
  };
  const copyProjectLink = async (p: Project) => {
    const url = `${window.location.origin}/brands/${brandId}/library?projectId=${p.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied",
        description: "Anyone with brand access can open this project view.",
      });
    } catch {
      toast({ title: "Couldn't copy link", description: url });
    }
  };
  const addProjectToWeeklyDelivery = (project: Project) => {
    setRequestedDeliveryProjectId(project.id);
    setManageProjectsOpen(false);
    setDeliveriesSheetOpen(true);
  };
  const [newProjectName, setNewProjectName] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);
  const pendingMoveAdIdRef = useRef<string | null>(null);

  // Reference data for the filter dropdowns.
  const { data: personas = [] } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  const { data: products = [] } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: offers = [] } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });
  const { data: angles = [] } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
  });
  const { data: adUnits = [] } = useQuery<AdUnitType[]>({
    queryKey: [`/api/brands/${brandId}/ad-units`],
    enabled: !!brandId,
  });
  const { data: projects = [], isLoading: projectsLoading } = useQuery<Project[]>({
    queryKey: [`/api/brands/${brandId}/projects`],
    enabled: !!brandId,
  });

  // Task #2185 — "?projectId=..." deep-link ("Copy link" on a project):
  // preselect that project's filter once the project list is loaded. If the
  // project isn't in the viewer's list (missing, archived, or master-only and
  // the viewer isn't a master), show a clean "not available" banner instead.
  const deepLinkProjectId = useMemo(
    () => new URLSearchParams(searchStr).get("projectId"),
    [searchStr],
  );
  const [projectLinkUnavailable, setProjectLinkUnavailable] = useState(false);
  useEffect(() => {
    if (!deepLinkProjectId || projectsLoading || !brandId) return;
    const found = projects.some((p) => p.id === deepLinkProjectId);
    if (found) {
      setProjectId(deepLinkProjectId);
      setProjectLinkUnavailable(false);
    } else {
      setProjectLinkUnavailable(true);
    }
    // Strip the param so refreshes/back-nav don't re-fire the jump; keep any
    // other params (e.g. adId) intact.
    const params = new URLSearchParams(searchStr);
    params.delete("projectId");
    const rest = params.toString();
    setLocation(`/brands/${brandId}/library${rest ? `?${rest}` : ""}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkProjectId, projectsLoading, projects, brandId]);

  // Distinct creators of this brand's ad generations, for the specific-
  // creator filter dropdown (Everyone / Mine / each person by name).
  const { data: creators = [] } = useQuery<LibraryCreator[]>({
    queryKey: [`/api/brands/${brandId}/generated-ads-creators`],
    enabled: !!brandId,
  });

  // Build the listing URL. Wrapping in useMemo keeps the React Query key
  // stable when nothing changed (otherwise we'd refetch on every render).
  const listUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("status", status);
    if (personaId !== "all") params.set("personaId", personaId);
    if (productId !== "all") params.set("productId", productId);
    if (offerId !== "all") params.set("offerId", offerId);
    if (angleId !== "all") params.set("narrativeAngleId", angleId);
    if (adUnitId !== "all") params.set("adUnitTypeId", adUnitId);
    if (projectId !== "all") params.set("projectId", projectId);
    if (creator === "mine" || creator === "others") {
      params.set("creator", creator);
    } else if (creator.startsWith("user:")) {
      params.set("creatorUserId", creator.slice("user:".length));
    }
    if (debouncedTag.trim()) params.set("tag", debouncedTag.trim());
    if (debouncedSearch.trim()) params.set("q", debouncedSearch.trim());
    return `/api/brands/${brandId}/generated-ads?${params.toString()}`;
  }, [brandId, status, personaId, productId, offerId, angleId, adUnitId, projectId, creator, debouncedTag, debouncedSearch]);

  // Task #1736 — the listing is paged per category: four independent
  // infinite queries (one per section) so each section can load pages on
  // demand from its own "View More" button. LIBRARY_CATEGORIES is a module
  // constant, so the hook order below is fixed.
  const adsQuery = useLibraryCategoryQuery(listUrl, brandId, "ads");
  const memesQuery = useLibraryCategoryQuery(listUrl, brandId, "memes");
  const slideshowsQuery = useLibraryCategoryQuery(listUrl, brandId, "slideshows");
  const videosQuery = useLibraryCategoryQuery(listUrl, brandId, "videos");
  const catQueries: Record<LibraryCategory, CategoryQuery> = {
    ads: adsQuery,
    memes: memesQuery,
    slideshows: slideshowsQuery,
    videos: videosQuery,
  };
  // Async helpers (View More / select-all / deep-link) need the CURRENT
  // query objects, not a stale render's closure.
  const catQueriesRef = useRef(catQueries);
  catQueriesRef.current = catQueries;

  const isLoading = LIBRARY_CATEGORIES.some((c) => catQueries[c.id].isLoading);
  const isFetching = LIBRARY_CATEGORIES.some((c) => catQueries[c.id].isFetching);
  // Task #2025 — a failed listing query must NOT masquerade as an empty
  // library ("No saved ads yet"). Any category query in error state surfaces
  // an error + retry instead of the empty state.
  const listingError = LIBRARY_CATEGORIES
    .map((c) => catQueries[c.id].error)
    .find((e): e is Error => !!e) ?? null;
  const retryListing = () => {
    for (const c of LIBRARY_CATEGORIES) {
      const q = catQueriesRef.current[c.id];
      if (q.isError) void q.refetch();
    }
  };

  // Reset the visible slices when the filtered listing changes.
  useEffect(() => {
    setVisibleCounts({});
  }, [listUrl]);

  // Group into Ads Express-style category sections (Ads / Memes /
  // Slideshows / Videos). `items` holds the LOADED rows; `total` is the full
  // matching count from page metadata (badges and remaining counts use it).
  // Categories with zero matches are hidden.
  const groupedByCategory = useMemo(() => {
    return LIBRARY_CATEGORIES
      .map((c) => ({
        ...c,
        items: flattenPages(catQueries[c.id].data),
        total: pagesTotal(catQueries[c.id].data),
      }))
      .filter((g) => g.total > 0 || g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adsQuery.data, memesQuery.data, slideshowsQuery.data, videosQuery.data]);

  // Flat list of all LOADED rows (selection, downloads, deep-links) and the
  // FULL matching total across categories (result count, select-all labels).
  const ads = useMemo(
    () => groupedByCategory.flatMap((g) => g.items),
    [groupedByCategory],
  );
  const totalCount = useMemo(
    () => groupedByCategory.reduce((n, g) => n + g.total, 0),
    [groupedByCategory],
  );

  // Fetch pages for one category until `pred` is satisfied (or the category
  // is exhausted). Returns the loaded rows. Used by View More, select-all and
  // the deep-link jump.
  const loadCategoryUntil = async (
    category: LibraryCategory,
    pred: (items: LibraryAd[]) => boolean,
  ): Promise<LibraryAd[]> => {
    const q = () => catQueriesRef.current[category];
    let data = q().data;
    let items = flattenPages(data);
    while (!pred(items)) {
      if (items.length >= pagesTotal(data)) break; // exhausted
      const r = await q().fetchNextPage();
      const next = flattenPages(r.data as InfiniteData<LibraryPage> | undefined);
      if (next.length <= items.length) break; // no progress — bail, never spin
      items = next;
      data = r.data as InfiniteData<LibraryPage>;
    }
    return items;
  };

  // "View More": grow the category's visible slice by one batch, fetching
  // the next server page(s) if the loaded rows don't cover the new slice.
  const showMoreInCategory = async (category: LibraryCategory, total: number) => {
    const current = visibleCounts[category] ?? CATEGORY_COLLAPSED_COUNT;
    const target = Math.min(current + VIEW_MORE_BATCH, total);
    setLoadingMoreCategory(category);
    try {
      await loadCategoryUntil(category, (items) => items.length >= target);
      setVisibleCounts((prev) => ({ ...prev, [category]: target }));
    } catch {
      // Keep the current slice; the button stays available to retry.
    } finally {
      setLoadingMoreCategory(null);
    }
  };

  const invalidate = () => {
    // Invalidate all variations of the listing for this brand so badge
    // counts in other tabs stay in sync as the user toggles state.
    return queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        typeof q.queryKey[0] === "string" &&
        (q.queryKey[0] as string).startsWith(`/api/brands/${brandId}/generated-ads`),
    });
  };

  // Ad id whose rename is currently in flight (disables the pencil).
  const [renamingAdId, setRenamingAdId] = useState<string | null>(null);
  const renameAd = async (ad: LibraryAd, name: string | null) => {
    setRenamingAdId(ad.id);
    try {
      await apiRequest("PATCH", `/api/generated-ads/${ad.id}`, { customName: name });
      invalidate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Couldn't rename", description: msg, variant: "destructive" });
      throw e;
    } finally {
      setRenamingAdId(null);
    }
  };

  const toggleSaved = async (ad: LibraryAd) => {
    try {
      const path = ad.isSaved ? "unsave" : "save";
      await apiRequest("POST", `/api/generated-ads/${ad.id}/${path}`, {});
      toast({ title: ad.isSaved ? "Removed from library" : "Saved" });
      invalidate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Couldn't update", description: msg, variant: "destructive" });
    }
  };

  const toggleWinner = async (ad: LibraryAd) => {
    try {
      await apiRequest("PATCH", `/api/generated-ads/${ad.id}`, {
        isWinner: !ad.isWinner,
      });
      toast({ title: ad.isWinner ? "Winner removed" : "Marked as winner" });
      invalidate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Couldn't update", description: msg, variant: "destructive" });
    }
  };

  const moveToProject = async (ad: LibraryAd, newProjectId: string | null) => {
    try {
      await apiRequest("PATCH", `/api/generated-ads/${ad.id}`, { projectId: newProjectId });
      toast({ title: newProjectId ? "Moved to project" : "Removed from project" });
      invalidate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Couldn't move", description: msg, variant: "destructive" });
    }
  };

  const toggleArchive = async (ad: LibraryAd) => {
    try {
      const path = ad.archivedAt ? "unarchive" : "archive";
      await apiRequest("POST", `/api/generated-ads/${ad.id}/${path}`, {});
      if (ad.archivedAt) {
        toast({ title: "Restored" });
      } else {
        // Capture this ad and this render's brand-scoped invalidation.
        const adId = ad.id;
        const undoArchive = async () => {
          try {
            await apiRequest("POST", `/api/generated-ads/${adId}/unarchive`, {});
            invalidate();
            toast({ title: "Restored", duration: 3000 });
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : String(e);
            toast({ title: "Couldn't update", description: msg, variant: "destructive" });
          }
        };
        toast({
          title: "Ad archived",
          duration: 8000,
          archiveUndo: true,
          action: (
            <ToastAction altText="Undo archiving this ad" onClick={undoArchive} data-testid={`undo-archive-${adId}`}>
              Undo
            </ToastAction>
          ),
        });
      }
      invalidate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Couldn't update", description: msg, variant: "destructive" });
    }
  };

  // Reframe-preview modal state. The server endpoint only renders the
  // image — saving (writing to R2 + library) is an explicit user choice
  // after the preview lands.
  type ReframeSource = {
    brandId: string;
    adUnitId: string | null;
    personaId: string | null;
    productId: string | null;
    narrativeAngleId: string | null;
    projectId: string | null;
    parentAdId: string;
    prompt: string | null;
    // Task #1517 — present ONLY when the server's safe_zone_reframe grant
    // is active for this user+brand; when set, the save call passes it
    // through so the persisted image is normalized to exact advertised
    // dimensions and the library row records its ratio.
    aspectRatio?: string | null;
  };
  type ReframeState =
    | { open: false }
    | {
        open: true;
        adId: string;
        adName: string;
        aspectRatio: AspectRatio;
        status: "loading" | "ready" | "error" | "saving";
        imageUrl?: string;
        source?: ReframeSource;
        errorMsg?: string;
      };
  const [reframeState, setReframeState] = useState<ReframeState>({ open: false });
  const reframeSaveInFlight = useRef(false);

  // Prompt-based "Edit" modal state. Reuses the same InpaintingModal as
  // Ads Express. On accept we mirror the edited image into R2 and replace
  // the saved ad in place (no new library entry, no kept history).
  const [editState, setEditState] = useState<
    { open: false } | { open: true; adId: string; imageUrl: string }
  >({ open: false });
  const imageEdits = useMyAdsImageEdits(brandId, () => {
    void invalidateApprovalStates(queryClient, brandId);
    void invalidate();
    invalidateUndoableAds(brandId);
  });

  const openEdit = (ad: LibraryAd) => {
    const url = ad.durableUrl ?? ad.imageUrlOriginal;
    if (!url || !ad.id || !canOpenPromptImageEditor({
      mediaType: ad.mediaType,
      imageUrl: url,
      adId: ad.id,
      status: ad.status,
    })) {
      toast({ title: "Can't edit", description: "This ad has no image yet.", variant: "destructive" });
      return;
    }
    setEditState({ open: true, adId: ad.id, imageUrl: url });
  };

  const acceptEdit = async (newImageUrl: string) => {
    if (!editState.open) return;
    const { adId } = editState;
    // Let errors propagate so the modal keeps itself open and shows the
    // message; the modal calls onClose() itself only on success.
    await apiRequest("POST", `/api/generated-ads/${adId}/edit-in-place`, {
      imageUrl: newImageUrl,
    });
    imageEdits.markApplied(adId);
    toast({ title: "Ad updated", description: "Your edit replaced the original image." });
    await invalidateApprovalStates(queryClient, brandId);
    await invalidate();
    // Task #2040 — the replaced image is now an undoable previous version.
    invalidateUndoableAds(brandId);
  };

  // Task #1689 — re-outpaint ONLY the top/bottom bands of a saved 9:16 ad
  // (the creative itself is extracted pixel-exact and never re-rendered).
  const fixBands = async (ad: LibraryAd) => {
    if (fixingBandsRef.current.has(ad.id)) return;
    fixingBandsRef.current.add(ad.id);
    setFixingBandIds((prev) => new Set(prev).add(ad.id));
    try {
      const res = await apiRequest("POST", `/api/generated-ads/${ad.id}/repair-bands`, {});
      const repaired = await res.json() as BandRepairResult;
      // Commit the returned durable source to every cached library page before
      // refetching. Open previews read these props too, not a provider URL.
      queryClient.setQueriesData<InfiniteData<LibraryPage>>({
        predicate: (q) => typeof q.queryKey[0] === "string" &&
          q.queryKey[0].startsWith(`/api/brands/${brandId}/generated-ads`),
      }, (cached) => !cached?.pages ? cached : ({
        ...cached,
        pages: cached.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => item.id !== ad.id ? item : ({
            ...item, durableUrl: repaired.durableUrl, mediaUrl: repaired.durableUrl,
            imageUrl: repaired.durableUrl, imageUrlOriginal: repaired.durableUrl,
            bandSource: repaired.bandSource, bandAttempts: repaired.bandAttempts,
          })),
        })),
      }));
      if (editState.open && editState.adId === ad.id) {
        setEditState({ open: true, adId: ad.id, imageUrl: repaired.durableUrl });
      }
      invalidateUndoableAds(brandId);
      await Promise.all([
        invalidateApprovalStates(queryClient, brandId),
        invalidate(),
        queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", ad.generationId] }),
      ]);
      toast(bandRepairNotice(repaired));
    } catch (e: unknown) {
      toast({
        title: "Band repair failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      fixingBandsRef.current.delete(ad.id);
      setFixingBandIds((prev) => {
        const next = new Set(prev);
        next.delete(ad.id);
        return next;
      });
    }
  };

  const reframe = async (ad: LibraryAd, aspectRatio: AspectRatio) => {
    setReframeState({
      open: true,
      adId: ad.id,
      adName: ad.customName?.trim() || ad.defaultNameSnapshot?.trim() || ad.adUnitNameSnapshot || "ad",
      aspectRatio,
      status: "loading",
    });
    try {
      const res = await apiRequest("POST", `/api/generated-ads/${ad.id}/reframe`, { aspectRatio });
      const data = (await res.json()) as { imageUrl: string; aspectRatio: string; source: ReframeSource };
      if (!data?.imageUrl) throw new Error("No image returned from reframe");
      try {
        await validateReframePreview(data.imageUrl, aspectRatio);
      } catch (error) {
        setReframeState({
          open: true, adId: ad.id,
          adName: ad.customName?.trim() || ad.defaultNameSnapshot?.trim() || ad.adUnitNameSnapshot || "ad",
          aspectRatio, status: "error", imageUrl: data.imageUrl, source: data.source,
          errorMsg: error instanceof Error ? error.message : String(error),
        });
        return;
      }
      setReframeState({
        open: true,
        adId: ad.id,
        adName: ad.customName?.trim() || ad.defaultNameSnapshot?.trim() || ad.adUnitNameSnapshot || "ad",
        aspectRatio,
        status: "ready",
        imageUrl: data.imageUrl,
        source: data.source,
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setReframeState({
        open: true,
        adId: ad.id,
        adName: ad.customName?.trim() || ad.defaultNameSnapshot?.trim() || ad.adUnitNameSnapshot || "ad",
        aspectRatio,
        status: "error",
        errorMsg: msg,
      });
    }
  };

  const saveReframe = async () => {
    if (reframeSaveInFlight.current) return;
    if (!reframeState.open || reframeState.status !== "ready" || !reframeState.imageUrl || !reframeState.source) return;
    const { source, imageUrl, aspectRatio, adId, adName } = reframeState;
    if (!source.adUnitId) {
      console.error("[reframe] save blocked: response is missing its ad-unit reference", {
        adId,
        brandId: source.brandId,
        parentAdId: adId,
      });
      toast({
        title: "We couldn't save this version",
        description: "Please try again. If it keeps happening, contact support and we'll help.",
        variant: "destructive",
      });
      return;
    }
    reframeSaveInFlight.current = true;
    setReframeState({ ...reframeState, status: "saving" });
    try {
      await apiRequest("POST", `/api/brands/${source.brandId}/library/save-generated-image`, {
        imageUrl,
        parentAdId: source.parentAdId,
        adUnitId: source.adUnitId,
        personaId: source.personaId,
        productId: source.productId,
        narrativeAngleId: source.narrativeAngleId,
        projectId: source.projectId,
        prompt: source.prompt,
        aspectRatio,
      });
      toast({ title: "Saved to My Ads", description: `New ${aspectRatio} version added to your library.` });
      setReframeState({ open: false });
      invalidate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Save failed", description: msg, variant: "destructive" });
      setReframeState({
        open: true,
        adId,
        adName,
        aspectRatio,
        status: "ready",
        imageUrl,
        source,
      });
    } finally {
      reframeSaveInFlight.current = false;
    }
  };

  // Task #2161 — "Copy as is" / "Copy & reframe". Both leave the original
  // untouched and add an independent saved duplicate to the grid (its own
  // R2 object — no shared storage). ratio=null means an exact duplicate;
  // a ratio runs the existing reframe pipeline and saves the result.
  // Task #2388 — "Edit Creative": one-time copy of an image ad into a
  // standalone Creative Editor project, then navigate to the editor. The
  // source ad is read-only to the editor and never modified. Per-ad pending
  // set so one import never locks the rest of the grid.
  const [creatingEditorIds, setCreatingEditorIds] = useState<Set<string>>(new Set());
  const editCreative = async (ad: LibraryAd) => {
    if (creatingEditorIds.has(ad.id)) return;
    setCreatingEditorIds((prev) => new Set(prev).add(ad.id));
    try {
      const res = await apiRequest("POST", "/api/creative-editor/projects/from-ad", {
        sourceAdId: ad.id,
      });
      const data = await res.json();
      const editorProjectId = data?.project?.id;
      if (!editorProjectId) throw new Error("The editor project wasn't created");
      setLocation(`/creative-editor/${editorProjectId}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({
        title: "Couldn't open the Creative Editor",
        description: msg,
        variant: "destructive",
      });
    } finally {
      // On success we navigate away; still clear the flag so the button is
      // usable if the user comes back via browser history.
      setCreatingEditorIds((prev) => {
        const next = new Set(prev);
        next.delete(ad.id);
        return next;
      });
    }
  };
  const [copyingIds, setCopyingIds] = useState<Set<string>>(new Set());
  const copyAd = async (ad: LibraryAd, ratio: AspectRatio | null) => {
    setCopyingIds((prev) => new Set(prev).add(ad.id));
    try {
      if (ratio) {
        await copyAndReframeAd(ad.id, ratio);
        toast({
          title: `Copy saved at ${ratio}`,
          description: "The original is untouched — both sizes are now in My Ads.",
        });
      } else {
        await duplicateAdAsIs(ad.id);
        toast({
          title: "Copy created",
          description: "An independent duplicate was added to My Ads.",
        });
      }
      invalidate();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Copy failed", description: msg, variant: "destructive" });
    } finally {
      setCopyingIds((prev) => {
        const next = new Set(prev);
        next.delete(ad.id);
        return next;
      });
    }
  };

  const closeReframeModal = () => {
    if (reframeState.open && reframeState.status === "loading") return; // don't allow close mid-render
    if (reframeState.open && reframeState.status === "saving") return;
    setReframeState({ open: false });
  };

  // Images remain small blob downloads; videos stream to disk instead.
  const downloadOne = async (url: string, name: string, ext: string = "png", adId?: string) => {
    const fileName = `${sanitizeFilename(name)}.${ext}`;
    try {
      const proxyUrl = `/api/img-download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(fileName)}`;
      if (ext === "mp4") {
        if (adId) {
          await downloadSavedVideo(adId, proxyUrl, fileName);
          return;
        }
        await downloadVideoToDisk(proxyUrl, fileName);
        return;
      }
      const res = await fetch(proxyUrl);
      if (!res.ok) {
        if (res.status === 401) throw new Error("Your session expired. Please sign in and try again.");
        const detail = (await res.text()).slice(0, 160);
        throw new Error(detail || `Download failed (${res.status})`);
      }
      const blob = await res.blob().catch(() => {
        throw new Error("Download interrupted or connection lost. Please try again.");
      });
      const expectedLength = Number(res.headers.get("content-length"));
      if (res.headers.has("content-length") && blob.size !== expectedLength) {
        throw new Error("Download was interrupted. Please try again.");
      }
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Download failed", description: msg, variant: "destructive" });
    }
  };

  // Single-ad download (same behavior as the per-card Download button).
  const downloadAd = (ad: LibraryAd) => {
    if (ad.mediaType === "slideshow") {
      // Server zips every slide in order. Same-origin attachment response —
      // a plain anchor click downloads it without opening a tab.
      const a = document.createElement("a");
      a.href = `/api/generated-ads/${ad.id}/download-slides`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    const isVideo = ad.mediaType === "video";
    const url = isVideo
      ? (ad.videoUrl ?? ad.mediaUrl)
      : (ad.durableUrl ?? ad.imageUrlOriginal);
    if (url) downloadOne(url, ad.customName?.trim() || ad.defaultNameSnapshot?.trim() || ad.adUnitNameSnapshot || "ad", isVideo ? "mp4" : "png", isVideo ? ad.id : undefined);
  };

  // Task #1585 — multi-select + bulk download. Select mode shows checkboxes
  // on every tile; one selection downloads like the single-download path,
  // multiple selections fetch one ZIP from the bulk endpoint.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  // Ads already downloaded in this select-mode session — lets "Select next
  // 100" walk through a >100-ad listing batch by batch without re-selecting
  // ads that were just ZIPped. Reset when leaving select mode.
  const [downloadedIds, setDownloadedIds] = useState<Set<string>>(new Set());
  const toggleSelected = (adId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(adId)) next.delete(adId);
      else next.add(adId);
      return next;
    });
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setDownloadedIds(new Set());
  };
  // Only count selections that are still in the filtered listing — filter
  // changes can hide previously-checked ads, and those must not download.
  const visibleSelectedIds = useMemo(
    () => ads.filter((a) => selectedIds.has(a.id)).map((a) => a.id),
    [ads, selectedIds],
  );
  // "Everything selected" compares against the FULL matching total — with
  // pagination, select-all first loads every remaining page (below), so a
  // complete selection really does cover the whole filtered set.
  const allVisibleSelected = totalCount > 0 && visibleSelectedIds.length === totalCount;
  // Server caps bulk ZIPs at 100 ads per request — batch bigger listings.
  const remainingIds = useMemo(
    () => ads.filter((a) => !downloadedIds.has(a.id)).map((a) => a.id),
    [ads, downloadedIds],
  );
  const overBulkLimit = visibleSelectedIds.length > BULK_DOWNLOAD_LIMIT;
  // Task #1736 — select-all semantics with pagination: selection targets the
  // FULL matching set, so select-all/select-next first fetch the remaining
  // pages on demand (spinner on the button while loading), then select.
  const [selectLoading, setSelectLoading] = useState(false);
  const loadAllCategories = async (): Promise<LibraryAd[]> => {
    const lists = await Promise.all(
      LIBRARY_CATEGORIES.map((c) => loadCategoryUntil(c.id, () => false)),
    );
    return lists.flat();
  };
  const selectNextBatch = async () => {
    setSelectLoading(true);
    try {
      const all = await loadAllCategories();
      const remaining = all.filter((a) => !downloadedIds.has(a.id)).map((a) => a.id);
      setSelectedIds(new Set(remaining.slice(0, BULK_DOWNLOAD_LIMIT)));
    } finally {
      setSelectLoading(false);
    }
  };
  const selectAllAds = async () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectLoading(true);
    try {
      const all = await loadAllCategories();
      setSelectedIds(new Set(all.map((a) => a.id)));
    } finally {
      setSelectLoading(false);
    }
  };
  // Per-category select-all toggle — adds/removes just that section's ads
  // (e.g. "My Ads", "Videos") without touching selections in other sections.
  // Loads the category's remaining pages first so "Select all" covers the
  // full category, not just the loaded rows.
  const toggleCategorySelected = async (category: LibraryCategory) => {
    setSelectLoading(true);
    try {
      const categoryAds = await loadCategoryUntil(category, () => false);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        const allSelected = categoryAds.length > 0 && categoryAds.every((a) => next.has(a.id));
        for (const a of categoryAds) {
          if (allSelected) next.delete(a.id);
          else next.add(a.id);
        }
        return next;
      });
    } finally {
      setSelectLoading(false);
    }
  };

  const bulkDownload = async () => {
    if (visibleSelectedIds.length === 0 || bulkDownloading) return;
    if (visibleSelectedIds.length > BULK_DOWNLOAD_LIMIT) {
      toast({
        title: `Too many ads selected`,
        description: `Downloads are limited to ${BULK_DOWNLOAD_LIMIT} ads at a time. Use "Select next ${BULK_DOWNLOAD_LIMIT}" to download in batches.`,
        variant: "destructive",
      });
      return;
    }
    if (visibleSelectedIds.length === 1) {
      const ad = ads.find((a) => a.id === visibleSelectedIds[0]);
      if (ad) downloadAd(ad);
      exitSelectMode();
      return;
    }
    setBulkDownloading(true);
    try {
      const res = await fetch("/api/generated-ads/bulk-download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ids: visibleSelectedIds }),
      });
      if (!res.ok) {
        let msg = `Download failed (${res.status})`;
        try {
          const j = await res.json();
          if (j?.message) msg = j.message;
        } catch { /* non-JSON error body */ }
        throw new Error(msg);
      }
      const skippedCount = Number(res.headers.get("X-Bulk-Download-Skipped-Count") || "0");
      let skippedNames: string[] = [];
      try {
        skippedNames = JSON.parse(decodeURIComponent(res.headers.get("X-Bulk-Download-Skipped") || "%5B%5D"));
      } catch { /* header parse failure — count still shown */ }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `my-ads-${new Date().toISOString().slice(0, 10)}-${visibleSelectedIds.length - skippedCount}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      const okCount = visibleSelectedIds.length - skippedCount;
      // Mark this batch as downloaded so "Select next 100" moves on to the
      // rest of the listing. Stay in select mode while un-downloaded ads
      // remain; exit once everything visible has been fetched.
      const newDownloaded = new Set(downloadedIds);
      for (const id of visibleSelectedIds) newDownloaded.add(id);
      const remainingAfter = ads.filter((a) => !newDownloaded.has(a.id)).length;
      if (skippedCount > 0) {
        toast({
          title: `Downloaded ${okCount} of ${visibleSelectedIds.length} ads`,
          description: `Couldn't fetch: ${skippedNames.slice(0, 5).join(", ")}${skippedCount > 5 ? "…" : ""}`,
          variant: "destructive",
        });
      } else {
        toast({
          title: `Downloaded ${okCount} ads`,
          description: remainingAfter > 0
            ? `ZIP saved. ${remainingAfter} ad${remainingAfter === 1 ? "" : "s"} left — use "Select next ${Math.min(remainingAfter, BULK_DOWNLOAD_LIMIT)}" for the next batch.`
            : "ZIP saved to your downloads.",
        });
      }
      if (remainingAfter > 0) {
        setDownloadedIds(newDownloaded);
        setSelectedIds(new Set());
      } else {
        exitSelectMode();
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Bulk download failed", description: msg, variant: "destructive" });
    } finally {
      setBulkDownloading(false);
    }
  };

  const clearFilters = () => {
    setPersonaId("all");
    setProductId("all");
    setOfferId("all");
    setAngleId("all");
    setAdUnitId("all");
    setProjectId("all");
    setCreator("everyone");
    setTag("");
    setSearch("");
  };

  // Task #1702 (1/2) — a deep-link arrived: switch to the Ads view and widen
  // the listing so the target can be found regardless of current filters.
  useEffect(() => {
    if (!deepLinkAdId) return;
    deepLinkRef.current = { adId: deepLinkAdId, phase: "all" };
    setView("ads");
    clearFilters();
    setStatus("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkAdId]);

  // Task #1702 (2/2), reworked for pagination (Task #1736) — once the
  // widened listing is loading under the right status, resolve the target
  // ad's category with a one-row id lookup, load that category's pages until
  // the ad is present, grow the visible slice to reveal it, scroll to it and
  // highlight it. Falls back to the archived listing, then to a toast.
  const deepLinkBusyRef = useRef(false);
  useEffect(() => {
    const dl = deepLinkRef.current;
    if (!dl || isLoading || isFetching) return;
    if (status !== dl.phase) return; // widened refetch not applied yet
    if (deepLinkBusyRef.current) return;
    deepLinkBusyRef.current = true;
    (async () => {
      try {
        // 1. One-row lookup by id (server-side `id` filter) to learn whether
        // the ad matches this phase's status and which category it's in —
        // without loading pages of every category to search for it.
        let target: LibraryAd | undefined;
        try {
          const res = await fetch(
            `/api/brands/${brandId}/generated-ads?status=${dl.phase}&id=${encodeURIComponent(dl.adId)}&paged=1&limit=1`,
            { credentials: "include" },
          );
          if (res.ok) {
            const page = (await res.json()) as LibraryPage;
            target = page.items?.[0];
          }
        } catch { /* treated as not found below */ }
        if (!target) {
          if (dl.phase === "all") {
            dl.phase = "archived";
            setStatus("archived");
            return;
          }
          deepLinkRef.current = null;
          setStatus("saved");
          // Strip the param via wouter so useSearch stays in sync (a raw
          // history.replaceState is invisible to it and blocks repeat jumps).
          setLocation(`/brands/${brandId}/library`, { replace: true });
          toast({
            title: "Ad not found",
            description: "That ad is no longer in your library — it may have been deleted.",
            variant: "destructive",
          });
          return;
        }
        // 2. Load the ad's category page by page until the row is present.
        const category = target.category;
        const items = await loadCategoryUntil(category, (list) =>
          list.some((a) => a.id === dl.adId),
        );
        const idxInCategory = items.findIndex((a) => a.id === dl.adId);
        if (idxInCategory === -1) {
          // Race: matched the lookup but vanished from the listing pages.
          deepLinkRef.current = null;
          setLocation(`/brands/${brandId}/library`, { replace: true });
          return;
        }
        deepLinkRef.current = null;
        // 3. Reveal it — grow the category's visible slice past its index.
        if (idxInCategory >= CATEGORY_COLLAPSED_COUNT) {
          setVisibleCounts((prev) => ({
            ...prev,
            [category]: Math.max(prev[category] ?? CATEGORY_COLLAPSED_COUNT, idxInCategory + 1),
          }));
        }
        showDeliveryDeepLinkHighlight(dl.adId);
        // Strip the param so refresh/back doesn't re-run the jump — via wouter
        // so useSearch updates and a repeat jump to the same ad re-triggers.
        setLocation(`/brands/${brandId}/library`, { replace: true });
        // Double rAF: wait for the (possibly expanded) grid to paint first.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document
              .querySelector(`[data-library-ad-id="${dl.adId}"]`)
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          });
        });
      } finally {
        deepLinkBusyRef.current = false;
      }
    })();
    // deepLinkAdId is a dep so a jump fired while the listing is ALREADY in
    // the widened state (e.g. a second jump from the notes dialog) still
    // resolves — otherwise no other dep changes and the effect never runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads, isLoading, isFetching, status, deepLinkAdId]);

  useEffect(() => () => {
    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
  }, []);

  // Task #1734 — AdCard is memoized; hand it referentially-stable handlers
  // (each takes the ad / ad id as an argument) so page-level state changes
  // don't invalidate every card's props and re-render the whole grid.
  const stableToggleSaved = useStableCallback(toggleSaved);
  const stableToggleWinner = useStableCallback(toggleWinner);
  const stableToggleArchive = useStableCallback(toggleArchive);
  const openSourceAdInPlace = useStableCallback(async (sourceAdId: string) => {
    let target = ads.find((ad) => ad.id === sourceAdId);
    if (!target) {
      try {
        const res = await fetch(
          `/api/brands/${brandId}/generated-ads?status=${status}&id=${encodeURIComponent(sourceAdId)}&paged=1&limit=1`,
          { credentials: "include" },
        );
        if (res.ok) {
          const page = (await res.json()) as LibraryPage;
          target = page.items?.[0];
        }
      } catch {
        // The friendly message below covers unavailable source rows.
      }
    }
    if (!target) {
      toast({
        title: "Source ad isn't in this view",
        description: "Adjust your current filters to show the ad it was iterated from.",
      });
      return;
    }

    const items = await loadCategoryUntil(
      target.category,
      (categoryItems) => categoryItems.some((ad) => ad.id === sourceAdId),
    );
    const sourceIndex = items.findIndex((ad) => ad.id === sourceAdId);
    if (sourceIndex === -1) {
      toast({
        title: "Source ad isn't in this view",
        description: "Adjust your current filters to show the ad it was iterated from.",
      });
      return;
    }
    setVisibleCounts((prev) => ({
      ...prev,
      [target.category]: Math.max(
        prev[target.category] ?? CATEGORY_COLLAPSED_COUNT,
        sourceIndex + 1,
      ),
    }));
    showDeliveryDeepLinkHighlight(sourceAdId);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document
          .querySelector(`[data-library-ad-id="${sourceAdId}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    });
  });
  const stableReframe = useStableCallback(reframe);
  const stableCopyAd = useStableCallback(copyAd);
  const stableFixBands = useStableCallback(fixBands);
  const stableRestoreOriginal = useStableCallback(restoreOriginal);
  const stableUndoEdit = useStableCallback(undoEdit);
  const stableRedoEdit = useStableCallback(redoEdit);
  const stableOpenEdit = useStableCallback(openEdit);
  const stableRenameAd = useStableCallback(renameAd);
  const stableDownloadAd = useStableCallback(downloadAd);
  const stableIterateFromAd = useStableCallback((ad: LibraryAd) => {
    setLocation(creativeIterationSeedHref(brandId, ad.id));
  });
  const stableEditCreative = useStableCallback(editCreative);
  const stableToggleSelected = useStableCallback(toggleSelected);
  const stableMoveToProject = useStableCallback((ad: LibraryAd, target: string | null | "__new") => {
    if (target === "__new") {
      pendingMoveAdIdRef.current = ad.id;
      setNewProjectOpen(true);
    } else {
      moveToProject(ad, target);
    }
  });
  // Task #1802 — edit a manual upload's tags/image (brand flag gated; the
  // dialog only opens for source="manual_upload" rows).
  const stableOpenManualEdit = useStableCallback((ad: LibraryAd) => {
    setManualEditAd(ad);
  });
  const stableOpenNotes = useStableCallback((ad: LibraryAd) => {
    setNotesDialogAd({
      adId: ad.id,
      adName: ad.customName?.trim() || ad.defaultNameSnapshot?.trim() || ad.adUnitNameSnapshot || null,
    });
  });

  const hasActiveFilters =
    personaId !== "all" ||
    productId !== "all" ||
    offerId !== "all" ||
    angleId !== "all" ||
    adUnitId !== "all" ||
    projectId !== "all" ||
    creator !== "everyone" ||
    !!tag.trim() ||
    !!search.trim();

  return (
    <div className="container mx-auto px-4 py-6 space-y-4 max-w-screen-2xl">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-page-title">
            My Ads
          </h1>
          <p className="text-sm text-muted-foreground">
            Saved, winning, and archived static ads from everyone on this brand.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {notesViewAvailable && (
            <div className="flex items-center rounded-md border border-border/60 p-0.5">
              <Button
                variant={view === "ads" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 text-xs"
                onClick={() => changeLibraryView("ads")}
                data-testid="button-view-ads"
              >
                <LayoutGrid className="h-3.5 w-3.5 mr-1" />
                Ads
              </Button>
              <Button
                variant={view === "notes" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 text-xs"
                onClick={() => changeLibraryView("notes")}
                data-testid="button-view-notes"
              >
                <MessageSquare className="h-3.5 w-3.5 mr-1" />
                Notes
              </Button>
            </div>
          )}
          {manualUploadEnabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUploadOpen(true)}
              data-testid="button-upload-images"
            >
              <ImagePlus className="h-4 w-4 mr-1.5" />
              Upload Creative
            </Button>
          )}
          {/* Weekly Deliverables — shown to master admins and brand members
              when the brand flag is on. */}
          {weeklyDeliverablesEnabled && pageIsMaster && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeliveriesSheetOpen(true)}
              data-testid="button-open-deliveries"
            >
              <PackageOpen className="h-4 w-4 mr-1.5" />
              Deliveries
            </Button>
          )}
          <Link href={`/brands/${brandId}/ads-express`}>
            <Button variant="outline" size="sm" data-testid="button-back-to-ads-express">
              <ExternalLink className="h-4 w-4 mr-1.5" />
              Open Ads Express
            </Button>
          </Link>
        </div>
      </header>

      {notesViewAvailable && view === "notes" && brandId && (
        <BrandNotesView
          key={brandId}
          brandId={brandId}
          masterReview={pageIsMaster}
          onOpenAd={(note) => {
            if (notesEnabled) setNotesDialogAd({ adId: note.adId, adName: note.adName ?? null });
            else setLocation(`/brands/${brandId}/library?adId=${note.adId}`);
          }}
        />
      )}

      {/* Task #1705 — manual media uploads (flagged). Opened from the
          "Upload Creative" header button; uploads land here in My Ads as
          saved, ready ads (R2-backed, "Manual upload" marker). */}
      {manualUploadEnabled && brandId && (
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogTitle>Upload Creative</DialogTitle>
            <ManualAdUpload
              brandId={brandId}
              personas={personas}
              products={products}
              offers={offers}
              angles={angles}
              onUploaded={() => {
                setUploadOpen(false);
                void queryClient.invalidateQueries({
                  predicate: (q) =>
                    Array.isArray(q.queryKey) &&
                    typeof q.queryKey[0] === "string" &&
                    (q.queryKey[0] as string).startsWith(`/api/brands/${brandId}/generated-ads`),
                });
              }}
            />
          </DialogContent>
        </Dialog>
      )}
      {/* Task #1802 — edit an existing manual upload (tags & image). */}
      {manualUploadEnabled && manualEditAd && (
        <ManualAdEditDialog
          ad={manualEditAd}
          personas={personas}
          products={products}
          offers={offers}
          angles={angles}
          onClose={() => setManualEditAd(null)}
          onSaved={invalidate}
        />
      )}
      <div className={view === "notes" ? "hidden" : "contents"}>
      <Card className="p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filters</span>
          {isFetching && !isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground ml-1" />
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant={selectMode ? "secondary" : "ghost"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
              data-testid="button-select-mode"
            >
              <CheckSquare className="h-3.5 w-3.5 mr-1" />
              {selectMode ? "Done" : "Select & Download"}
            </Button>
            <SafeZoneToggle active={showSafeZone} onToggle={() => setShowSafeZone((v) => !v)} />
            {pageIsMaster && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={startBulkBandRepair}
                disabled={!!bulkBandJob?.running}
                title="Master only: scan saved 9:16 ads and re-render blurry fallback bands (creatives untouched)"
                data-testid="button-bulk-fix-bands"
              >
                {bulkBandJob?.running ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    Fixing bands… {bulkBandJob.processed}/{bulkBandJob.total}
                  </>
                ) : (
                  <>
                    <Wand2 className="h-3.5 w-3.5 mr-1" />
                    Fix 9:16 bands
                  </>
                )}
              </Button>
            )}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-7 text-xs"
                data-testid="button-clear-filters"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
          <FilterSelect
            value={status}
            onChange={(v) => setStatus(v as LibraryStatus)}
            placeholder="Status"
            testId="select-status"
            options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            includeAll={false}
          />
          <FilterSelect
            value={personaId}
            onChange={setPersonaId}
            placeholder="Persona"
            testId="select-persona"
            options={personas.map((p) => ({ value: p.id, label: p.name }))}
          />
          <FilterSelect
            value={productId}
            onChange={setProductId}
            placeholder="Product"
            testId="select-product"
            options={products.map((p) => ({ value: p.id, label: p.name }))}
          />
          <FilterSelect
            value={offerId}
            onChange={setOfferId}
            placeholder="Offers"
            testId="select-offering"
            options={offers.map((offer) => ({ value: offer.id, label: offer.name }))}
          />
          <FilterSelect
            value={angleId}
            onChange={setAngleId}
            placeholder="Narrative angle"
            testId="select-angle"
            options={angles.map((a) => ({ value: a.id, label: a.name }))}
          />
          <FilterSelect
            value={adUnitId}
            onChange={setAdUnitId}
            placeholder="Ad unit"
            testId="select-ad-unit"
            options={adUnits
              .filter((u) => u.formatType === "static")
              .map((u) => ({ value: u.id, label: u.name }))}
          />
          <div className="flex items-center gap-1 min-w-0">
            <div className="flex-1 min-w-0">
              <FilterSelect
                value={projectId}
                onChange={setProjectId}
                placeholder="Project"
                testId="select-project"
                options={[
                  { value: "none", label: "(no project)" },
                  ...projects.map((p) => ({ value: p.id, label: p.name })),
                ]}
              />
            </div>
            {/* Task #1948 — master-only "Manage projects" dialog opener
                (rename/delete live inside the dialog). */}
            {pageIsMaster && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0"
                title="Manage projects"
                onClick={() => setManageProjectsOpen(true)}
                data-testid="button-manage-projects"
              >
                <Folder className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          <FilterSelect
            value={creator}
            onChange={(v) => setCreator(v as CreatorFilter)}
            placeholder="Creator"
            testId="select-creator"
            options={[
              { value: "everyone", label: "Everyone" },
              { value: "mine", label: "Mine" },
              ...creators.map((c) => ({ value: `user:${c.id}`, label: c.name })),
            ]}
            includeAll={false}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search ad name or prompt…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 pl-8 text-sm"
              data-testid="input-search"
            />
          </div>
          <Input
            placeholder="Tag (exact match)"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className="h-9 text-sm"
            data-testid="input-tag"
          />
        </div>
      </Card>

      {/* Task #2185 — a project deep-link that isn't visible to this viewer
          (missing, archived, or master-only) gets a clean banner, not an
          error or a data leak. */}
      {projectLinkUnavailable && (
        <Card className="p-4 flex items-start gap-3" data-testid="banner-project-unavailable">
          <ImageOff className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">This project isn't available</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              The project link you opened doesn't exist or isn't visible to
              your account. You're seeing your full ad library instead.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setProjectLinkUnavailable(false)}
            data-testid="button-dismiss-project-unavailable"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </Card>
      )}

      {status === "archived" && (
        <p className="text-sm text-muted-foreground" data-testid="archive-retention-helper">{ARCHIVE_RETENTION_HELPER}</p>
      )}
      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading your ads…
        </div>
      ) : listingError && totalCount === 0 ? (
        <div className="text-center py-16 space-y-3" data-testid="listing-error">
          <ImageOff className="h-8 w-8 mx-auto text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">Couldn't load your ads</p>
            <p className="text-xs text-muted-foreground mt-1">
              {listingError.message || "Something went wrong loading your ads."}{" "}
              Your ads are safe — try again in a moment.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={retryListing}
            data-testid="button-retry-listing"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Retry
          </Button>
        </div>
      ) : totalCount === 0 ? (
        <EmptyState status={status} hasFilters={hasActiveFilters} brandId={brandId!} />
      ) : (
        <>
          <p className="text-xs text-muted-foreground" data-testid="text-result-count">
            {totalCount} ad{totalCount === 1 ? "" : "s"}
          </p>
          {selectMode && (
            <div
              className="sticky top-2 z-20 flex items-center gap-2 flex-wrap rounded-lg border bg-background/95 backdrop-blur px-3 py-2 shadow-sm"
              data-testid="toolbar-bulk-select"
            >
              <span className="text-sm font-medium tabular-nums" data-testid="text-selection-count">
                {visibleSelectedIds.length} selected
              </span>
              {totalCount > BULK_DOWNLOAD_LIMIT ? (
                // Listings over the per-ZIP cap download in batches — this
                // walks through the remaining (not-yet-downloaded) ads,
                // loading any not-yet-fetched pages first.
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={selectNextBatch}
                  disabled={bulkDownloading || selectLoading || (downloadedIds.size >= totalCount)}
                  data-testid="button-select-next-batch"
                >
                  {selectLoading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  {`Select next ${Math.min(totalCount - downloadedIds.size, BULK_DOWNLOAD_LIMIT)}${downloadedIds.size > 0 ? ` (${totalCount - downloadedIds.size} left)` : ` of ${totalCount}`}`}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={selectAllAds}
                  disabled={bulkDownloading || selectLoading}
                  data-testid="button-select-all"
                >
                  {selectLoading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  {allVisibleSelected ? "Deselect all" : `Select all (${totalCount})`}
                </Button>
              )}
              {visibleSelectedIds.length > 0 && !allVisibleSelected && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setSelectedIds(new Set())}
                  disabled={bulkDownloading}
                  data-testid="button-clear-selection"
                >
                  Clear selection
                </Button>
              )}
              <div className="ml-auto flex items-center gap-2">
                {overBulkLimit && (
                  <span className="text-xs text-muted-foreground" data-testid="text-bulk-limit-hint">
                    Max {BULK_DOWNLOAD_LIMIT} per ZIP
                  </span>
                )}
                {visibleSelectedIds.length > 0 && (
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    onClick={bulkDownload}
                    disabled={bulkDownloading || overBulkLimit}
                    data-testid="button-bulk-download"
                  >
                    {bulkDownloading ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5 mr-1" />
                    )}
                    {bulkDownloading
                      ? "Preparing ZIP…"
                      : `Download ${visibleSelectedIds.length === 1 ? "1 ad" : `${visibleSelectedIds.length} ads (ZIP)`}`}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={exitSelectMode}
                  disabled={bulkDownloading}
                  data-testid="button-exit-select-mode"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
          <div className="space-y-8" data-testid="grid-library">
            {groupedByCategory.map(({ id, label, items, total }) => {
              const visual = CATEGORY_VISUALS[id];
              const visibleCount = Math.min(visibleCounts[id] ?? CATEGORY_COLLAPSED_COUNT, total);
              const visibleItems = items.slice(0, visibleCount);
              const hasMore = visibleItems.length < total;
              const hiddenCount = total - visibleItems.length;
              const canCollapse = visibleCount > CATEGORY_COLLAPSED_COUNT;
              const loadingMore = loadingMoreCategory === id;
              const categoryFullySelected =
                total > 0 && items.length >= total && items.every((a) => selectedIds.has(a.id));
              return (
                <div key={id} data-testid={`section-category-${id}`}>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={["h-9 w-9 rounded-lg flex items-center justify-center shrink-0", visual.accent].join(" ")}>
                        {visual.icon}
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="text-base font-bold tracking-tight" data-testid={`text-category-label-${id}`}>
                          {label}
                        </h3>
                        <Badge
                          variant="secondary"
                          className="h-5 px-1.5 text-[10px] font-bold tabular-nums"
                          data-testid={`badge-category-count-${id}`}
                        >
                          {total}
                        </Badge>
                        {selectMode && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground shrink-0"
                            onClick={() => toggleCategorySelected(id)}
                            disabled={bulkDownloading || selectLoading}
                            data-testid={`button-select-category-${id}`}
                          >
                            {categoryFullySelected
                              ? "Deselect all"
                              : `Select all (${total})`}
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className={["hidden sm:block h-px flex-1 ml-3 opacity-30", visual.bar].join(" ")} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {visibleItems.map((ad) => (
                      <div
                        key={ad.id}
                        data-library-ad-id={ad.id}
                        data-library-deeplink-highlighted={
                          ad.id === highlightedAdId ? "true" : undefined
                        }
                        className={
                          ad.id === highlightedAdId
                            ? "relative rounded-lg ring-4 ring-primary ring-offset-4 ring-offset-background shadow-xl transition-all duration-500"
                            : undefined
                        }
                      >
                      <AdCard
                        ad={ad}
                        imageEdit={imageEdits.states[ad.id]}
                        imageEditOutcome={imageEdits.applied[ad.id]}
                        onRetryImageEditSave={imageEdits.retrySave}
                        onDiscardImageEdit={imageEdits.discard}
                        projects={projects}
                        isReframing={reframeState.open && reframeState.adId === ad.id && (reframeState.status === "loading" || reframeState.status === "saving")}
                        onToggleSaved={stableToggleSaved}
                        onToggleWinner={stableToggleWinner}
                        onToggleArchive={stableToggleArchive}
                        onReframe={stableReframe}
                        onCopy={stableCopyAd}
                        isCopying={copyingIds.has(ad.id)}
                        onFixBands={pageIsMaster || (bandRepairEnabled && ad.isSaved) ? stableFixBands : undefined}
                        isFixingBands={fixingBandIds.has(ad.id)}
                        onRestoreOriginal={
                          restorableAdIds.has(ad.id) && ad.mediaType !== "video" && ad.mediaType !== "slideshow"
                            ? stableRestoreOriginal
                            : undefined
                        }
                        isRestoring={restoringIds.has(ad.id)}
                        onUndoEdit={
                          undoableAdIds.has(ad.id) && ad.mediaType !== "video" && ad.mediaType !== "slideshow"
                            ? stableUndoEdit
                            : undefined
                        }
                        isUndoing={undoingIds.has(ad.id)}
                        onRedoEdit={
                          redoableAdIds.has(ad.id) && ad.mediaType !== "video" && ad.mediaType !== "slideshow"
                            ? stableRedoEdit
                            : undefined
                        }
                        isRedoing={redoingIds.has(ad.id)}
                        showSafeZone={showSafeZone}
                        onEdit={
                          canOpenPromptImageEditor({
                            mediaType: ad.mediaType,
                            imageUrl: ad.durableUrl ?? ad.imageUrlOriginal,
                            adId: ad.id,
                            status: ad.status,
                          })
                            ? stableOpenEdit
                            : undefined
                        }
                        onEditManual={
                          manualUploadEnabled && (ad as any).source === "manual_upload"
                            ? stableOpenManualEdit
                            : undefined
                        }
                        onEditCreative={creativeEditorEnabled ? stableEditCreative : undefined}
                        isCreatingEditor={creatingEditorIds.has(ad.id)}
                        onRename={stableRenameAd}
                        renaming={renamingAdId === ad.id}
                        onOpenSourceAd={openSourceAdInPlace}
                        onMoveToProject={stableMoveToProject}
                        onDownload={stableDownloadAd}
                        onIterateFromAd={
                          ad.eligibleIterationSource &&
                          !ad.archivedAt &&
                          (ad.mediaType !== "video" || creativeIterationsVideoEnabled)
                            ? stableIterateFromAd
                            : undefined
                        }
                        selectMode={selectMode}
                        selected={selectedIds.has(ad.id)}
                        onToggleSelect={stableToggleSelected}
                        notesCount={noteCounts[ad.id] ?? 0}
                        onOpenNotes={notesEnabled && ad.isSaved ? stableOpenNotes : undefined}
                        approvalStatus={
                          weeklyDeliverablesEnabled && ad.id
                            ? approvalStates.get(ad.id) ?? null
                            : null
                        }
                      />
                      </div>
                    ))}
                  </div>
                  {(hasMore || canCollapse) && (
                    <div className="mt-2.5 flex justify-center gap-2">
                      {hasMore && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => showMoreInCategory(id, total)}
                          disabled={loadingMore}
                          data-testid={`button-category-toggle-${id}`}
                        >
                          {loadingMore ? (
                            <>
                              Loading…
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            </>
                          ) : (
                            <>
                              View More (+{hiddenCount})
                              <ChevronDown className="h-3.5 w-3.5" />
                            </>
                          )}
                        </Button>
                      )}
                      {canCollapse && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
                          onClick={() =>
                            setVisibleCounts((prev) => ({ ...prev, [id]: CATEGORY_COLLAPSED_COUNT }))
                          }
                          disabled={loadingMore}
                          data-testid={`button-category-collapse-${id}`}
                        >
                          View Less
                          <ChevronUp className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      </div>

      {notesDialogAd && brandId && (
        <AdNotesDialog
          open={!!notesDialogAd}
          onOpenChange={(o) => { if (!o) setNotesDialogAd(null); }}
          adId={notesDialogAd.adId}
          brandId={brandId}
          adName={notesDialogAd.adName}
        />
      )}

      {/* Weekly Deliverables sheet — master admins and brand members with
          the weekly_deliverables flag. */}
      {weeklyDeliverablesEnabled && pageIsMaster && brandId && (
        <WeeklyDeliveriesSheet
          brandId={brandId}
          isMaster={pageIsMaster}
          open={deliveriesSheetOpen}
          onOpenChange={setDeliveriesSheetOpen}
          projects={projects}
          deepLinkAdId={deepLinkAdId}
          approvalStates={approvalStates}
          initialProjectId={requestedDeliveryProjectId}
          onInitialProjectHandled={() => setRequestedDeliveryProjectId(null)}
          deepLinkDeliveryId={requestedDeliveryId}
          onDeliveryDeepLinkHandled={() => setRequestedDeliveryId(null)}
          onOpenAd={(adId) => {
            // Give immediate visual confirmation before the URL-driven lookup
            // widens filters and scrolls the card into view.
            showDeliveryDeepLinkHighlight(adId);
            setDeliveriesSheetOpen(false);
            setLocation(`/brands/${brandId}/library?adId=${encodeURIComponent(adId)}`);
          }}
        />
      )}

      <Dialog
        open={newProjectOpen}
        onOpenChange={(o) => {
          if (!o) {
            setNewProjectOpen(false);
            setNewProjectName("");
            pendingMoveAdIdRef.current = null;
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="dialog-new-project">
          <DialogTitle>New project</DialogTitle>
          <div className="space-y-3 pt-2">
            <Input
              autoFocus
              placeholder="e.g. Spring Campaign 2026"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              disabled={creatingProject}
              data-testid="input-new-project-name"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setNewProjectOpen(false);
                  setNewProjectName("");
                  pendingMoveAdIdRef.current = null;
                }}
                disabled={creatingProject}
                data-testid="button-cancel-new-project"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const name = newProjectName.trim();
                  if (!name || !brandId) return;
                  // Snapshot the pending ad id BEFORE any awaits so that
                  // closing the dialog (which clears the ref) cannot race
                  // with the move PATCH we're about to send.
                  const moveAdId = pendingMoveAdIdRef.current;
                  setCreatingProject(true);
                  try {
                    const res = await apiRequest("POST", `/api/brands/${brandId}/projects`, { name });
                    const created = (await res.json()) as { id: string; name: string };
                    if (!created?.id) {
                      throw new Error("Server did not return a project id");
                    }
                    // Optimistically push the new project into the cache so
                    // the filter dropdown + per-card pickers show it
                    // immediately, then kick off a background refetch.
                    // Dedupe in case a concurrent refetch already inserted it.
                    queryClient.setQueryData<Project[]>(
                      [`/api/brands/${brandId}/projects`],
                      (prev) => {
                        const next = created as Project;
                        if (!prev) return [next];
                        return prev.some((p) => p.id === next.id) ? prev : [next, ...prev];
                      },
                    );
                    queryClient.invalidateQueries({
                      queryKey: [`/api/brands/${brandId}/projects`],
                    });
                    // Move directly by adId so a stale `ads` closure (e.g. a
                    // background refetch dropping rows from the list) cannot
                    // silently skip the PATCH.
                    if (moveAdId) {
                      try {
                        await apiRequest("PATCH", `/api/generated-ads/${moveAdId}`, {
                          projectId: created.id,
                        });
                        toast({ title: "Moved to project" });
                        invalidate();
                      } catch (moveErr: unknown) {
                        const moveMsg = moveErr instanceof Error ? moveErr.message : String(moveErr);
                        console.error("[ad-library] move-to-new-project failed", moveErr);
                        toast({ title: "Couldn't move", description: moveMsg, variant: "destructive" });
                      }
                    }
                    setNewProjectOpen(false);
                    setNewProjectName("");
                    pendingMoveAdIdRef.current = null;
                    toast({ title: "Project created", description: created.name });
                  } catch (e: unknown) {
                    const msg = e instanceof Error ? e.message : String(e);
                    console.error("[ad-library] create project failed", e);
                    toast({ title: "Couldn't create project", description: msg, variant: "destructive" });
                  } finally {
                    setCreatingProject(false);
                  }
                }}
                disabled={creatingProject || !newProjectName.trim()}
                data-testid="button-create-project"
              >
                {creatingProject && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task #1948 — master-only "Manage projects" dialog: lists every
          active project with rename/delete row actions. */}
      <Dialog open={manageProjectsOpen} onOpenChange={setManageProjectsOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-manage-projects">
          <DialogTitle>Manage projects</DialogTitle>
          <div className="pt-2 space-y-1 max-h-[60vh] overflow-y-auto">
            {projects.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No projects yet. Create one from an ad card's project picker or
                when generating ads.
              </p>
            ) : (
              projects.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/60"
                  data-testid={`row-manage-project-${p.id}`}
                >
                  <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="flex-1 min-w-0 truncate text-sm">{p.name}</span>
                  {p.visibility === "master_only" && (
                    <Badge
                      variant="secondary"
                      className="shrink-0 text-[10px] px-1.5"
                      data-testid={`badge-master-only-${p.id}`}
                    >
                      Master only
                    </Badge>
                  )}
                  {/* Task #2185 — visibility toggle: everyone ⇄ master-only. */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    title={
                      p.visibility === "master_only"
                        ? "Make visible to everyone"
                        : "Hide from non-masters (master only)"
                    }
                    disabled={projectActionBusy}
                    onClick={() => toggleProjectVisibility(p)}
                    data-testid={`button-toggle-visibility-${p.id}`}
                  >
                    {p.visibility === "master_only" ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  {/* Task #2185 — copy a deep-link to this project's saved-ads
                      view. Disabled (with an explanatory tooltip) while the
                      project is master-only: recipients couldn't open it. */}
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex" tabIndex={p.visibility === "master_only" ? 0 : -1}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            title={p.visibility === "master_only" ? undefined : "Copy link to this project"}
                            disabled={p.visibility === "master_only"}
                            onClick={() => copyProjectLink(p)}
                            data-testid={`button-copy-project-link-${p.id}`}
                          >
                            <Link2 className="h-3.5 w-3.5" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {p.visibility === "master_only" && (
                        <TooltipContent side="top">
                          Make this project visible to everyone to share a link
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  {weeklyDeliverablesEnabled && pageIsMaster && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      title="Add to this week's delivery"
                      onClick={() => addProjectToWeeklyDelivery(p)}
                      data-testid={`button-add-project-to-delivery-${p.id}`}
                    >
                      <PackagePlus className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    title="Rename project"
                    onClick={() => {
                      setRenameProjectName(p.name);
                      setRenameProjectTarget(p);
                    }}
                    data-testid={`button-rename-project-${p.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-destructive"
                    title="Delete project"
                    onClick={() => setDeleteProjectTarget(p)}
                    data-testid={`button-delete-project-${p.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Task #1948 — master-only project rename dialog. */}
      <Dialog
        open={!!renameProjectTarget}
        onOpenChange={(o) => { if (!o && !projectActionBusy) setRenameProjectTarget(null); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="dialog-rename-project">
          <DialogTitle>Rename project</DialogTitle>
          <div className="space-y-3 pt-2">
            <Input
              autoFocus
              value={renameProjectName}
              onChange={(e) => setRenameProjectName(e.target.value)}
              disabled={projectActionBusy}
              data-testid="input-rename-project-name"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setRenameProjectTarget(null)}
                disabled={projectActionBusy}
                data-testid="button-cancel-rename-project"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const target = renameProjectTarget;
                  const name = renameProjectName.trim();
                  if (!target || !name) return;
                  setProjectActionBusy(true);
                  try {
                    await apiRequest("PATCH", `/api/projects/${target.id}`, { name });
                    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/projects`] });
                    // Listing rows carry a projectName snapshot — refresh them
                    // so cards show the new name.
                    invalidate();
                    setRenameProjectTarget(null);
                    toast({ title: "Project renamed", description: name });
                  } catch (e: unknown) {
                    const msg = e instanceof Error ? e.message : String(e);
                    toast({ title: "Couldn't rename project", description: msg, variant: "destructive" });
                  } finally {
                    setProjectActionBusy(false);
                  }
                }}
                disabled={projectActionBusy || !renameProjectName.trim()}
                data-testid="button-save-rename-project"
              >
                {projectActionBusy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task #1948 — master-only project delete (archive) confirmation. */}
      <Dialog
        open={!!deleteProjectTarget}
        onOpenChange={(o) => { if (!o && !projectActionBusy) setDeleteProjectTarget(null); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="dialog-delete-project">
          <DialogTitle>Delete project?</DialogTitle>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-muted-foreground">
              "{deleteProjectTarget?.name}" will be removed from every project
              list and selector. The ads inside it are NOT deleted — they stay
              in your library, just no longer grouped under this project.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteProjectTarget(null)}
                disabled={projectActionBusy}
                data-testid="button-cancel-delete-project"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  const target = deleteProjectTarget;
                  if (!target) return;
                  setProjectActionBusy(true);
                  try {
                    await apiRequest("DELETE", `/api/projects/${target.id}`, {});
                    // If the filter (or any picker) was pointed at the deleted
                    // project, fall back to "all" so the page never shows an
                    // empty, un-reselectable state.
                    if (projectId === target.id) setProjectId("all");
                    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/projects`] });
                    invalidate();
                    setDeleteProjectTarget(null);
                    toast({ title: "Project deleted", description: `${target.name} was archived.` });
                  } catch (e: unknown) {
                    const msg = e instanceof Error ? e.message : String(e);
                    toast({ title: "Couldn't delete project", description: msg, variant: "destructive" });
                  } finally {
                    setProjectActionBusy(false);
                  }
                }}
                disabled={projectActionBusy}
                data-testid="button-confirm-delete-project"
              >
                {projectActionBusy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Delete project
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reframe preview modal — shows a spinner while nano-banana-2/edit
          is rendering, then the resulting image with Save / Discard. The
          ad is only written to the library if the user clicks Save. */}
      <Dialog
        open={reframeState.open}
        onOpenChange={(o) => { if (!o) closeReframeModal(); }}
      >
        <DialogContent className="sm:max-w-2xl" data-testid="dialog-reframe-preview">
          <DialogTitle>
            {reframeState.open
              ? `Reframe to ${reframeState.aspectRatio}`
              : "Reframe"}
          </DialogTitle>
          {reframeState.open && (
            <div className="space-y-4 pt-2">
              <p className="text-xs text-muted-foreground">Reframe uses Nano Banana 2.1 / 2.0 (global setting).</p>
              {(reframeState.status === "loading" || reframeState.status === "saving") && (
                <div
                  className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground"
                  data-testid="reframe-status-loading"
                >
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <p className="text-sm">
                    {reframeState.status === "saving"
                      ? "Saving to your library…"
                      : `Reframing "${reframeState.adName}" to ${reframeState.aspectRatio}…`}
                  </p>
                  {reframeState.status === "loading" && (
                    <p className="text-xs">This usually takes 10–30 seconds.</p>
                  )}
                </div>
              )}
              {(reframeState.status === "ready" || reframeState.status === "error") && reframeState.imageUrl && (
                <>
                  <div className="flex items-center justify-center bg-muted/40 rounded-md p-2">
                    <AdImage
                      src={reframeState.imageUrl}
                      alt={`Reframed ${reframeState.aspectRatio} ad`}
                      className="max-h-[60vh] w-auto rounded"
                      data-testid="img-reframe-preview"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    This preview hasn't been saved yet. Save it to add it to
                    your library, or discard to drop it.
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={closeReframeModal}
                      data-testid="button-reframe-discard"
                    >
                      Discard
                    </Button>
                    <Button
                      onClick={saveReframe}
                      disabled={reframeState.status !== "ready"}
                      data-testid="button-reframe-save"
                    >
                      <Bookmark className="h-4 w-4 mr-1.5" />
                      Save to My Ads
                    </Button>
                  </div>
                </>
              )}
              {reframeState.status === "error" && (
                <div className="space-y-3" data-testid="reframe-status-error">
                  <p className="text-sm text-destructive">
                    Reframe failed: {reframeState.errorMsg ?? "Unknown error"}
                  </p>
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={closeReframeModal}
                      data-testid="button-reframe-close-error"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Prompt-based Edit modal — same component Ads Express uses. On
          accept the edited image replaces the saved ad in place. */}
      {editState.open && (
        <InpaintingModal
          key={editState.adId}
          open={editState.open}
          onClose={() => setEditState({ open: false })}
          imageUrl={editState.imageUrl}
          adId={editState.adId}
          editTarget={{ surface: "my-ads", adId: editState.adId, brandId }}
          brandId={brandId}
          onAccept={acceptEdit}
        />
      )}
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  placeholder,
  options,
  testId,
  includeAll = true,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  testId: string;
  includeAll?: boolean;
}) {
  return (
    <SearchableSelect
      value={value}
      onValueChange={onChange}
      placeholder={placeholder}
      testId={testId}
      triggerClassName="h-9 text-sm"
      options={[
        ...(includeAll ? [{ value: "all", label: `All ${placeholder.toLowerCase()}` }] : []),
        ...options,
      ]}
    />
  );
}

// Tags we never want to surface in the My Ads grid — they leak the
// underlying model name from older save flows.
const HIDDEN_TAGS = new Set(["nano-banana-2", "gpt-image-2", "gpt-image-1"]);

// All reframe targets we expose, sourced from the centralized aspect-ratio
// list (shared/aspectRatios.ts) so this surface stays in sync with Ads
// Express and the other generation surfaces.
const ALL_RATIOS: Array<{ value: AspectRatio; label: string }> =
  AD_ASPECT_RATIO_OPTIONS.map((o) => ({ value: o.value, label: o.label }));

// Snap a measured w/h to one of our known ratios when it's within ~4% —
// covers the small rounding the model produces (e.g. 1080×1351). Returns
// null for anything else (custom ad-unit dimensions). The targets are
// spaced far enough apart that 4% tolerance never causes a collision.
function snapAspectRatio(width: number, height: number): AspectRatio | null {
  if (!width || !height) return null;
  const r = width / height;
  const TOL = 0.04;
  if (Math.abs(r - 1080 / 1350) < TOL) return "4:5";
  if (Math.abs(r - 1080 / 1920) < TOL) return "9:16";
  if (Math.abs(r - 1) < TOL) return "1:1";
  if (Math.abs(r - 1920 / 1080) < TOL) return "16:9";
  if (Math.abs(r - 1080 / 1620) < TOL) return "2:3";
  return null;
}

// Compact "Jul 27, 2026"-style date line for the card. Saved ads show
// their saved date; unsaved ads fall back to the creation date. API rows
// serialize timestamps as ISO strings.
export function ArchiveRetentionLabels({ ad }: { ad: Pick<LibraryAd, "id" | "archivedAt" | "isSaved" | "isWinner"> }) {
  if (!ad.archivedAt) return null;
  const retention = archiveRetention(ad.archivedAt, ad.isSaved, ad.isWinner);
  if (!retention) return null;
  return (
    <div className="mt-1 text-[11px]" data-testid={`archive-retention-${ad.id}`}>
      <p className="text-muted-foreground">Archived {retention.daysAgo} days ago</p>
      <p className={retention.warning && !retention.kept ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}>
        {retention.kept ? "Kept" : `Restore by ${retention.deadline.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
      </p>
    </div>
  );
}

function AdCardDate({ ad }: { ad: LibraryAd }) {
  const useSaved = !!(ad.isSaved && ad.savedAt);
  const raw = useSaved ? ad.savedAt : ad.createdAt;
  if (!raw) return null;
  const date = raw instanceof Date ? raw : new Date(raw);
  if (isNaN(date.getTime())) return null;
  const compact = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return (
    <p
      className="text-[11px] text-muted-foreground mt-0.5 w-fit"
      data-testid={`text-ad-date-${ad.id}`}
    >
      {useSaved ? "Saved" : "Created"} {compact}
    </p>
  );
}

const AdCard = memo(function AdCard({
  ad,
  imageEdit,
  imageEditOutcome,
  onRetryImageEditSave,
  onDiscardImageEdit,
  projects,
  isReframing,
  onFixBands,
  isFixingBands,
  onRestoreOriginal,
  isRestoring,
  onUndoEdit,
  isUndoing,
  onRedoEdit,
  isRedoing,
  onEditManual,
  onEditCreative,
  isCreatingEditor,
  onToggleSaved,
  onToggleWinner,
  onToggleArchive,
  onReframe,
  onCopy,
  isCopying,
  onEdit,
  onMoveToProject,
  onDownload,
  onIterateFromAd,
  onRename,
  renaming,
  onOpenSourceAd,
  showSafeZone,
  selectMode,
  selected,
  onToggleSelect,
  notesCount = 0,
  onOpenNotes,
  approvalStatus = null,
}: {
  ad: LibraryAd;
  imageEdit?: MyAdsEditState;
  imageEditOutcome?: string;
  onRetryImageEditSave: (jobId: string) => void;
  onDiscardImageEdit: (jobId: string) => void;
  projects: Project[];
  isReframing: boolean;
  // Task #1689 — only provided when the brand's safe_zone_enhanced_render
  // flag is on and the ad is saved; the button additionally shows only for
  // client-detected 9:16 images.
  onFixBands?: (ad: LibraryAd) => void;
  isFixingBands?: boolean;
  // Task #1669 — only provided when this ad has a surviving pre-degradation
  // original from the 2026-07 edit bug; all other ads never see the action.
  onRestoreOriginal?: (ad: LibraryAd) => void;
  isRestoring?: boolean;
  // Task #2040 — only provided when this ad's last edit is undoable (its
  // superseded image still survives in quarantine). Restores one step back.
  onUndoEdit?: (ad: LibraryAd) => void;
  isUndoing?: boolean;
  onRedoEdit?: (ad: LibraryAd) => void;
  isRedoing?: boolean;
  // Task #1802 — only provided when the brand's ads_express_manual_upload
  // flag is on AND this row is a manual upload; opens the tag/image editor.
  onEditManual?: (ad: LibraryAd) => void;
  // Task #2388 — only provided when the brand's creative_editor flag is on;
  // copies this image ad into a standalone editor project and opens it.
  onEditCreative?: (ad: LibraryAd) => void;
  isCreatingEditor?: boolean;
  onToggleSaved: (ad: LibraryAd) => void;
  onToggleWinner: (ad: LibraryAd) => void;
  onToggleArchive: (ad: LibraryAd) => void;
  onReframe: (ad: LibraryAd, aspectRatio: AspectRatio) => void;
  // Task #2161 — duplicate this ad. ratio=null → "Copy as is" (exact
  // duplicate, own storage); a ratio → "Copy & reframe" (new saved ad at
  // that ratio, original untouched).
  onCopy: (ad: LibraryAd, ratio: AspectRatio | null) => void;
  isCopying: boolean;
  onEdit?: (ad: LibraryAd) => void;
  // target is a project id, "none" to clear, or "__new" to trigger
  // the parent's "+ New project" dialog (which then moves the ad).
  onMoveToProject: (ad: LibraryAd, target: string | null | "__new") => void;
  onDownload: (ad: LibraryAd) => void;
  onIterateFromAd?: (ad: LibraryAd) => void;
  onRename: (ad: LibraryAd, name: string | null) => void;
  renaming: boolean;
  onOpenSourceAd: (sourceAdId: string) => void;
  showSafeZone?: boolean;
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: (adId: string) => void;
  // Task #1625 — ad notes. Both only provided when the `ad_notes` flag is
  // on and the ad is saved.
  notesCount?: number;
  onOpenNotes?: (ad: LibraryAd) => void;
  // Weekly Deliverables — per-ad client feedback derived from frozen reviews:
  // current image decisions render Approved / Rejected; decisions
  // on older image snapshots render as earlier-version feedback.
  approvalStatus?: AdApprovalStatus | null;
}) {
  // Task #1777 — the render-engine line is a master-admin-only diagnostic.
  const { user: authUser } = useAuth();
  const isMasterViewer = authUser?.isMaster === true;
  const { toast: cardToast } = useToast();
  // Task #1798 — master-only per-ad action: rebuild this ad's default name
  // to the current convention (adds the PersonaOS segment before the date
  // code). Custom names are never touched; ineligible ads report why.
  const [regeneratingName, setRegeneratingName] = useState(false);
  const regenerateName = async () => {
    setRegeneratingName(true);
    try {
      const res = await apiRequest("POST", `/api/generated-ads/${ad.id}/regenerate-name`, {});
      const data = await res.json();
      cardToast({
        title: data.changed ? "Name regenerated" : "Name already up to date",
        description: data.name,
      });
      if (data.changed) {
        queryClient.invalidateQueries({
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            typeof q.queryKey[0] === "string" &&
            (q.queryKey[0] as string).includes("/generated-ads"),
        });
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      cardToast({ title: "Couldn't regenerate name", description: msg, variant: "destructive" });
    } finally {
      setRegeneratingName(false);
    }
  };
  // Task #2439 — master-only "Fresh prompt" regen from My Ads: same route
  // as the Ads Express tiles (re-runs the Sim prompt workflow with the ad's
  // original run inputs, then renders anew). Server is the real gate
  // (fail-closed requireMaster + terminal-only CAS → 409 while in flight).
  // The library row stays visible (listing filters on isSaved, not status);
  // we poll the deep-link lookup until the new render lands, then refresh.
  const [freshRegening, setFreshRegening] = useState(false);
  const freshRegenPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => () => {
    if (freshRegenPollRef.current) clearInterval(freshRegenPollRef.current);
  }, []);
  const freshRegen = async () => {
    setFreshRegening(true);
    try {
      await apiRequest("POST", `/api/generated-ads/${ad.id}/fresh-prompt-regen`, {});
      cardToast({
        title: "Fresh prompt regeneration started",
        description: "Re-running the prompt workflow, then rendering a new creative. The image updates here when it's ready.",
      });
      if (freshRegenPollRef.current) clearInterval(freshRegenPollRef.current);
      freshRegenPollRef.current = setInterval(async () => {
        try {
          const res = await fetch(
            `/api/brands/${ad.brandId}/generated-ads?status=all&id=${ad.id}`,
            { credentials: "include" },
          );
          if (!res.ok) return;
          const rows = await res.json();
          const row = Array.isArray(rows) ? rows[0] : rows?.items?.[0];
          if (!row || row.status === "pending" || row.status === "rendering" || row.status === "queued") return;
          if (freshRegenPollRef.current) clearInterval(freshRegenPollRef.current);
          freshRegenPollRef.current = null;
          setFreshRegening(false);
          if (row.status === "ready") {
            invalidateApprovalStates(queryClient, ad.brandId);
            cardToast({ title: "Fresh creative ready", description: "The ad was regenerated with a new prompt." });
          } else {
            cardToast({ title: "Fresh prompt regen failed", description: row.errorMessage || "The regeneration did not complete.", variant: "destructive" });
          }
          queryClient.invalidateQueries({
            predicate: (q) =>
              Array.isArray(q.queryKey) &&
              typeof q.queryKey[0] === "string" &&
              (q.queryKey[0] as string).includes("/generated-ads"),
          });
        } catch {
          // transient poll failure — keep polling
        }
      }, 5000);
    } catch (e: unknown) {
      setFreshRegening(false);
      const msg = e instanceof Error ? e.message : String(e);
      cardToast({ title: "Couldn't start fresh regeneration", description: msg, variant: "destructive" });
    }
  };
  const isVideo = ad.mediaType === "video";
  const isSlideshow = ad.mediaType === "slideshow";
  const slideImages = isSlideshow && Array.isArray(ad.slideImages) ? ad.slideImages : [];
  // Which slide the card's carousel is showing (slideshow entries only).
  const [slideIndex, setSlideIndex] = useState(0);
  const clampedSlideIndex = Math.min(slideIndex, Math.max(0, slideImages.length - 1));
  const url = isVideo
    ? (ad.videoUrl ?? ad.mediaUrl ?? null)
    : isSlideshow
      ? (slideImages[clampedSlideIndex]?.url ?? ad.durableUrl ?? ad.imageUrlOriginal)
      : (ad.durableUrl ?? ad.imageUrlOriginal);
  const isArchived = !!ad.archivedAt;
  const previewName = ad.customName?.trim() || ad.defaultNameSnapshot?.trim()
    || ad.adUnitNameSnapshot || (isSlideshow ? "Slideshow" : "Generated ad");

  // Detect the current aspect ratio from the loaded image's natural
  // dimensions — this works for every historical row too. Task #2213: when
  // client-side detection hasn't resolved (image not loaded yet / measure
  // failed) fall back to the server-known ratio column, so the menus still
  // hide the current ratio (the server also short-circuits same-ratio
  // reframes to a lossless copy as a backstop). Anything outside the known
  // targets is labelled "Custom" and the dropdown shows all targets.
  const [measuredRatio, setMeasuredRatio] = useState<AspectRatio | null>(null);
  const serverRatio: AspectRatio | null =
    typeof (ad as any).aspectRatio === "string" &&
    ALL_RATIOS.some((r) => r.value === (ad as any).aspectRatio)
      ? ((ad as any).aspectRatio as AspectRatio)
      : null;
  const currentRatio = measuredRatio ?? serverRatio;
  const reframeOptions = currentRatio
    ? ALL_RATIOS.filter((r) => r.value !== currentRatio)
    : ALL_RATIOS;
  const ratioBadgeLabel = currentRatio ?? "Custom";

  return (
    <Card
      className={[
        "overflow-hidden flex flex-col",
        selectMode && selected ? "ring-2 ring-primary" : "",
      ].join(" ")}
      data-testid={`card-ad-${ad.id}`}
    >
      <div className="relative aspect-square bg-muted/40">
        {selectMode && (
          <>
            {/* Click-anywhere-on-the-image toggle target. Sits above the
                media but below the checkbox so both toggle selection. */}
            <button
              type="button"
              onClick={() => onToggleSelect(ad.id)}
              className="absolute inset-0 z-10 cursor-pointer"
              aria-label={selected ? "Deselect ad" : "Select ad"}
              data-testid={`overlay-select-${ad.id}`}
            />
            <div className="absolute top-2 left-2 z-20">
              <Checkbox
                checked={selected}
                onCheckedChange={() => onToggleSelect(ad.id)}
                className="h-5 w-5 bg-background/90 border-2 shadow"
                aria-label={selected ? "Deselect ad" : "Select ad"}
                data-testid={`checkbox-select-${ad.id}`}
              />
            </div>
          </>
        )}
        {url ? (
          isVideo ? (
            <video
              src={url}
              controls
              playsInline
              preload="metadata"
              className="w-full h-full object-contain bg-black"
              data-testid={`video-ad-${ad.id}`}
            />
          ) : isSlideshow ? (
            // Slideshow entry: in-card carousel with prev/next arrows and a
            // "n / N" position indicator.
            <div className="absolute inset-0" data-testid={`carousel-slideshow-${ad.id}`}>
              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
              <AdImage
                src={url}
                alt={`${ad.customName?.trim() || ad.defaultNameSnapshot?.trim() || ad.adUnitNameSnapshot || "Slideshow"} — slide ${clampedSlideIndex + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid={`img-slide-${ad.id}-${clampedSlideIndex}`}
              />
              {slideImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setSlideIndex((i) => (i - 1 + slideImages.length) % slideImages.length)
                    }
                    className="absolute z-10 left-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
                    aria-label="Previous slide"
                    data-testid={`button-slide-prev-${ad.id}`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setSlideIndex((i) => (i + 1) % slideImages.length)}
                    className="absolute z-10 right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
                    aria-label="Next slide"
                    data-testid={`button-slide-next-${ad.id}`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
              <div
                className="absolute bottom-1.5 right-1.5 rounded-full bg-black/60 text-white text-[11px] px-2 py-0.5 tabular-nums"
                data-testid={`text-slide-position-${ad.id}`}
              >
                {clampedSlideIndex + 1} / {slideImages.length}
              </div>
            </div>
          ) : showSafeZone && currentRatio === "9:16" ? (
            // Safe-zone preview: swap the square crop for the FULL 9:16 frame
            // (centered) so the 4:5 band guides line up with the real pixels.
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="relative h-full aspect-[9/16]">
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                <AdImage
                  src={url}
                  alt={ad.customName?.trim() || ad.defaultNameSnapshot?.trim() || ad.adUnitNameSnapshot || "Generated ad"}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  data-testid={`img-ad-${ad.id}`}
                />
                <SafeZoneOverlay />
              </div>
            </div>
          ) : (
            // eslint-disable-next-line jsx-a11y/img-redundant-alt
            <AdImage
              src={url}
              alt={ad.customName?.trim() || ad.defaultNameSnapshot?.trim() || ad.adUnitNameSnapshot || "Generated ad"}
              className="w-full h-full object-cover"
              loading="lazy"
              data-testid={`img-ad-${ad.id}`}
              onLoad={(e) => {
                const img = e.currentTarget;
                setMeasuredRatio(snapAspectRatio(img.naturalWidth, img.naturalHeight));
              }}
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ImageOff className="h-8 w-8" />
          </div>
        )}
        {!isVideo && !selectMode && (
          <LibraryImagePreview
            url={url ?? null}
            name={isSlideshow ? `${previewName} — slide ${clampedSlideIndex + 1}` : previewName}
            adId={ad.id}
          />
        )}
        {imageEdit?.busy && (
          <div role="status" aria-live="polite" className="absolute inset-0 z-10 bg-black/60 flex flex-col items-center justify-center gap-2 text-white pointer-events-none" data-testid={`overlay-image-edit-${ad.id}`}>
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-sm font-semibold">Editing…</span>
            <span className="text-xs">{imageEdit.label}</span>
          </div>
        )}
        {imageEdit && !imageEdit.busy && (
          <div className="absolute bottom-0 inset-x-0 z-20 bg-background/95 p-2 text-xs" data-testid={`outcome-image-edit-${ad.id}`}>
            <span role={imageEdit.error ? "alert" : "status"}>{imageEdit.label}</span>
            <div className="flex gap-2 mt-1">
              {imageEdit.canRetrySave && <Button size="sm" variant="outline" onClick={() => onRetryImageEditSave(imageEdit.jobId)}>Retry save</Button>}
              <Button size="sm" variant="ghost" onClick={() => onDiscardImageEdit(imageEdit.jobId)}>Discard edit</Button>
            </div>
          </div>
        )}
        {!imageEdit && imageEditOutcome && (
          <span role="status" aria-live="polite" data-testid={`complete-image-edit-${ad.id}`} className="absolute bottom-2 left-2 z-20 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm pointer-events-none">
            <CheckCircle2 className="h-4 w-4" />
            {imageEditOutcome}
          </span>
        )}
        {/* Task #2439 — while a fresh-prompt regen is in flight, dim the
            old creative and show progress ON the image itself. */}
        {freshRegening && (
          <div
            className="absolute inset-0 z-10 bg-black/50 flex flex-col items-center justify-center gap-2 text-white"
            data-testid={`overlay-fresh-regen-${ad.id}`}
          >
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-xs font-medium">Regenerating with fresh prompt…</span>
          </div>
        )}
        {/* In select mode the checkbox owns the top-left corner, so the
            status badges shift right to avoid overlapping it. */}
        <div className={["absolute top-2 flex gap-1.5", selectMode ? "left-9" : "left-2"].join(" ")}>
          {ad.isWinner && (
            <Badge className="bg-amber-500 hover:bg-amber-500 text-white border-transparent gap-1">
              <Trophy className="h-3 w-3" />
              Winner
            </Badge>
          )}
          {ad.isSaved && !ad.isWinner && (
            <Badge variant="secondary" className="gap-1">
              <Bookmark className="h-3 w-3" />
              Saved
            </Badge>
          )}
          {isArchived && (
            <Badge variant="outline" className="gap-1">
              <Archive className="h-3 w-3" />
              Archived
            </Badge>
          )}
          {/* Weekly Deliverables — client-feedback badges. Shown even when the ad is
              already a winner so reviewers see the external signal. Renders
              current or earlier-version approval/rejection via the shared
              ApprovalBadge. */}
          <ApprovalBadge status={approvalStatus} adId={ad.id} />
        </div>
        {onOpenNotes && (
          <button
            type="button"
            onClick={() => onOpenNotes(ad)}
            className="absolute bottom-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-black/60 hover:bg-black/75 text-white text-[11px] px-2 py-1 tabular-nums"
            aria-label="Notes"
            data-testid={`button-notes-${ad.id}`}
          >
            <MessageSquare className="h-3 w-3" />
            {notesCount > 0 ? notesCount : "Note"}
          </button>
        )}
      </div>

      <div className="p-3 flex-1 flex flex-col gap-2">
        <div>
          <h3 className="text-sm font-medium leading-snug">
            <InlineRenameTitle
              customName={ad.customName}
              fallbackName={ad.defaultNameSnapshot?.trim() || ad.adUnitNameSnapshot || "Untitled ad"}
              onSave={(name) => onRename(ad, name)}
              saving={renaming}
              className="[&>span:first-child]:line-clamp-2"
              inputClassName="text-sm font-medium"
              testId={`ad-name-${ad.id}`}
            />
          </h3>
          {(ad as any).source === "manual_upload" && (
            <span
              className="mt-1 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
              data-testid={`badge-manual-${ad.id}`}
            >
              Manual upload
            </span>
          )}
           {ad.personaCharacterSelectorEnabled === true && (
             <Badge
               variant={ad.selectedPersonaCharacterNameSnapshot ? "secondary" : "outline"}
               className="mt-1 max-w-full text-[10px]"
               data-testid={`badge-saved-ad-character-${ad.id}`}
             >
               <span className="truncate">
                 {ad.selectedPersonaCharacterNameSnapshot
                   ? `Character: ${ad.selectedPersonaCharacterNameSnapshot}`
                   : "Character unavailable"}
               </span>
             </Badge>
           )}
          {/* Task #2181 — this ad was generated under a sale/discount. */}
          {!!(ad as any).salesDiscountNameSnapshot?.trim() && (
            <span
              className="mt-1 inline-flex max-w-full items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400 truncate"
              title={`Sale: ${(ad as any).salesDiscountNameSnapshot.trim()}`}
              data-testid={`badge-sale-${ad.id}`}
            >
              Sale: {(ad as any).salesDiscountNameSnapshot.trim()}
            </span>
          )}
          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
            {[ad.personaNameSnapshot, ad.productNameSnapshot, ad.angleNameSnapshot]
              .filter(Boolean)
              .join(" · ") || "—"}
          </p>
          {/* Which template produced this ad — shown only when the tile's
              visible title is NOT already the template name (a rename or the
              auto convention name is displayed instead). */}
          {(ad as any).source !== "manual_upload" &&
            !!ad.adUnitNameSnapshot?.trim() &&
            !!(ad.customName?.trim() || ad.defaultNameSnapshot?.trim()) &&
            ad.adUnitNameSnapshot.trim() !==
              (ad.customName?.trim() || ad.defaultNameSnapshot?.trim()) && (
              <p
                className="text-[11px] text-muted-foreground mt-0.5 truncate"
                title={`Template: ${ad.adUnitNameSnapshot.trim()}`}
                data-testid={`text-template-${ad.id}`}
              >
                Template: {ad.adUnitNameSnapshot.trim()}
              </p>
            )}
          {ad.createdByName && (
            <p
              className="text-[11px] text-muted-foreground mt-0.5 truncate"
              data-testid={`text-ad-creator-${ad.id}`}
            >
              made by {ad.createdByName}
            </p>
          )}
          <AdCardDate ad={ad} />
          <ArchiveRetentionLabels ad={ad} />
          {ad.sourcePerformanceAd && (
            <Link
              href={creativePerformanceAdHref(ad.brandId!, ad.sourcePerformanceAd.adId)}
              title={`Iterated from: ${ad.sourcePerformanceAd.adName}`}
              className="mt-1 inline-flex max-w-full items-center gap-1 text-[11px] font-medium text-primary hover:underline"
              data-testid={`link-performance-source-${ad.id}`}
            >
              <Link2 className="h-3 w-3 shrink-0" />
              <span className="truncate">Source in Creative Performance</span>
            </Link>
          )}
          {ad.parentAdId && (
            <button
              type="button"
              onClick={() => onOpenSourceAd(ad.parentAdId!)}
              className="mt-1 inline-flex max-w-full items-center gap-1 text-[11px] font-medium text-primary hover:underline"
              data-testid={`button-iteration-source-${ad.id}`}
            >
              <Link2 className="h-3 w-3 shrink-0" />
              <span className="truncate">Iterated from another ad</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {isVideo ? (
            <>
              <Video className="h-3 w-3 shrink-0" />
              <span data-testid={`text-media-type-${ad.id}`}>
                Type: <span className="font-medium text-foreground">Video</span>
              </span>
            </>
          ) : isSlideshow ? (
            <>
              <Images className="h-3 w-3 shrink-0" />
              <span data-testid={`text-media-type-${ad.id}`}>
                Type:{" "}
                <span className="font-medium text-foreground">
                  Slideshow ({slideImages.length} slides)
                </span>
              </span>
            </>
          ) : (
            <>
              <Crop className="h-3 w-3 shrink-0" />
              <span data-testid={`text-aspect-ratio-${ad.id}`}>
                Aspect ratio:{" "}
                <span className="font-medium text-foreground">{ratioBadgeLabel}</span>
              </span>
            </>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <Folder className="h-3 w-3 text-muted-foreground shrink-0" />
          <SearchableSelect
            value={ad.projectId ?? "none"}
            onValueChange={(v) => {
              if (v === "__new") onMoveToProject(ad, "__new");
              else if (v === "none") onMoveToProject(ad, null);
              else onMoveToProject(ad, v);
            }}
            placeholder="(no project)"
            testId={`select-project-${ad.id}`}
            triggerClassName="h-7 text-[11px] flex-1"
            options={[
              { value: "none", label: "(no project)" },
              ...projects.map((p) => ({ value: p.id, label: p.name })),
              { value: "__new", label: "+ New project…", testId: `select-project-new-${ad.id}` },
            ]}
          />
        </div>
        {(() => {
          const visibleTags = Array.isArray(ad.tags)
            ? ad.tags.filter((t) => t && !HIDDEN_TAGS.has(t))
            : [];
          if (visibleTags.length === 0) return null;
          return (
            <div className="flex flex-wrap gap-1">
              {visibleTags.slice(0, 4).map((t) => (
                <Badge key={t} variant="outline" className="text-[10px] py-0 px-1.5">
                  {t}
                </Badge>
              ))}
            </div>
          );
        })()}

        <div className="grid grid-cols-2 gap-1.5 mt-auto pt-1">
          {onIterateFromAd && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onIterateFromAd(ad)}
              className="h-8 text-xs col-span-2"
              data-testid={`button-iterate-from-ad-${ad.id}`}
            >
              <Wand2 className="h-3.5 w-3.5 mr-1" />
              Iterate from this ad
            </Button>
          )}
          <Button
            variant={ad.isWinner ? "default" : "outline"}
            size="sm"
            onClick={() => onToggleWinner(ad)}
            className="h-8 text-xs"
            data-testid={`button-winner-${ad.id}`}
          >
            <Trophy className="h-3.5 w-3.5 mr-1" />
            {ad.isWinner ? "Winner" : "Mark winner"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDownload(ad)}
            disabled={!url}
            className="h-8 text-xs"
            data-testid={`button-download-${ad.id}`}
          >
            <Download className="h-3.5 w-3.5 mr-1" />
            Download
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onToggleSaved(ad)}
            className="h-8 text-xs"
            data-testid={`button-toggle-saved-${ad.id}`}
          >
            <Bookmark className="h-3.5 w-3.5 mr-1" />
            {ad.isSaved ? "Unsave" : "Save"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onToggleArchive(ad)}
            className="h-8 text-xs"
            data-testid={`button-toggle-archive-${ad.id}`}
          >
            {isArchived ? (
              <>
                <ArchiveRestore className="h-3.5 w-3.5 mr-1" />
                Restore
              </>
            ) : (
              <>
                <Archive className="h-3.5 w-3.5 mr-1" />
                Archive
              </>
            )}
          </Button>
          {/* Edit + Reframe are single-image-only operations — hidden for
              video and slideshow rows. */}
          {!isVideo && !isSlideshow && (
            <>
              {/* Task #1669 — only ads with a surviving pre-degradation
                  original (2026-07 edit bug) get this action. */}
              {onRestoreOriginal && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRestoreOriginal(ad)}
                  disabled={isRestoring}
                  className="h-8 text-xs col-span-2"
                  data-testid={`button-restore-original-${ad.id}`}
                >
                  {isRestoring ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Restoring…
                    </>
                  ) : (
                    <>
                      <History className="h-3.5 w-3.5 mr-1" />
                      Restore original
                    </>
                  )}
                </Button>
              )}
              {/* Task #2040 — one-step undo of the last accepted edit;
                  only ads whose superseded image still survives get it. */}
              {onUndoEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUndoEdit(ad)}
                  disabled={isUndoing}
                  className="h-8 text-xs col-span-2"
                  title="Restore the version this ad showed before its last edit"
                  data-testid={`button-undo-edit-${ad.id}`}
                >
                  {isUndoing ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Undoing…
                    </>
                  ) : (
                    <>
                      <Undo2 className="h-3.5 w-3.5 mr-1" />
                      Undo edit
                    </>
                  )}
                </Button>
              )}
              {/* Task #2040 follow-up — separate Redo (never a toggling Undo):
                  brings back the edit an undo just removed. */}
              {onRedoEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRedoEdit(ad)}
                  disabled={isRedoing}
                  className="h-8 text-xs col-span-2"
                  title="Bring back the edit you just undid"
                  data-testid={`button-redo-edit-${ad.id}`}
                >
                  {isRedoing ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Redoing…
                    </>
                  ) : (
                    <>
                      <Redo2 className="h-3.5 w-3.5 mr-1" />
                      Redo
                    </>
                  )}
                </Button>
              )}
              {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(ad)}
                className="h-8 text-xs col-span-2"
                data-testid={`button-edit-${ad.id}`}
              >
                <Pencil className="h-3.5 w-3.5 mr-1" />
                Edit
              </Button>
              )}
              {/* Task #2388 — "Edit Creative": copies this ad into a
                  standalone layered editor project (flag-gated by the
                  parent; the source ad is never modified). */}
              {onEditCreative && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEditCreative(ad)}
                  disabled={isCreatingEditor || !url}
                  className="h-8 text-xs col-span-2"
                  title="Open a copy of this ad in the layered Creative Editor"
                  data-testid={`button-edit-creative-${ad.id}`}
                >
                  {isCreatingEditor ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Opening…
                    </>
                  ) : (
                    <>
                      <Layers className="h-3.5 w-3.5 mr-1" />
                      Edit Creative
                    </>
                  )}
                </Button>
              )}
              {/* Task #1802 — manual uploads only (flag-gated by the parent):
                  retag persona/product/angle or replace the uploaded image. */}
              {onEditManual && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEditManual(ad)}
                  className="h-8 text-xs col-span-2"
                  data-testid={`button-edit-manual-${ad.id}`}
                >
                  <ImagePlus className="h-3.5 w-3.5 mr-1" />
                  Edit upload
                </Button>
              )}
              {/* Task #1798/#2204 — master-only, static ads AND saved videos
                  (memes/slideshow slides keep their own names): rebuild this
                  ad's default name to the PersonaOS convention (custom names
                  stay untouched). */}
              {/* Task #2439 — master-only fresh-prompt creative regen for
                  run-generated static ads (manual uploads have no run inputs;
                  the server 400s them regardless). */}
              {isMasterViewer &&
                ad.category === "ads" &&
                (ad as any).source !== "manual_upload" &&
                ad.adUnitTypeId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={freshRegen}
                  disabled={freshRegening}
                  className="h-8 text-xs col-span-2"
                  data-testid={`button-fresh-regen-${ad.id}`}
                  title="Re-run the prompt workflow with this ad's original run inputs, then render a NEW creative (unlike edits/re-rolls that reuse the stored prompt)"
                >
                  {freshRegening ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Regenerating…
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-3.5 w-3.5 mr-1" />
                      Fresh prompt regen
                    </>
                  )}
                </Button>
              )}
              {isMasterViewer && (ad.category === "ads" || ad.category === "videos") && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={regenerateName}
                  disabled={regeneratingName}
                  className="h-8 text-xs col-span-2"
                  data-testid={`button-regenerate-name-${ad.id}`}
                >
                  {regeneratingName ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Regenerating…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 mr-1" />
                      Regenerate name
                    </>
                  )}
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isReframing || !url || reframeOptions.length === 0}
                    className="h-8 text-xs col-span-2"
                    data-testid={`button-reframe-${ad.id}`}
                  >
                    {isReframing ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        Reframing…
                      </>
                    ) : (
                      <>
                        <Crop className="h-3.5 w-3.5 mr-1" />
                        Reframe to…
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {reframeOptions.map((r) => (
                    <DropdownMenuItem
                      key={r.value}
                      onClick={() => onReframe(ad, r.value)}
                      data-testid={`menuitem-reframe-${r.value.replace(":", "x")}-${ad.id}`}
                    >
                      {r.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Task #2161 — copy menu: exact duplicate or duplicate-then-
                  reframe. Hidden for manual uploads (server rejects them). */}
              {(ad as any).source !== "manual_upload" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isCopying || !url}
                      className="h-8 text-xs col-span-2"
                      data-testid={`button-copy-${ad.id}`}
                    >
                      {isCopying ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          Copying…
                        </>
                      ) : (
                        <>
                          <CopyIcon className="h-3.5 w-3.5 mr-1" />
                          Copy…
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => onCopy(ad, null)}
                      data-testid={`menuitem-copy-as-is-${ad.id}`}
                    >
                      Copy as is
                    </DropdownMenuItem>
                    {reframeOptions.map((r) => (
                      <DropdownMenuItem
                        key={r.value}
                        onClick={() => onCopy(ad, r.value)}
                        data-testid={`menuitem-copy-reframe-${r.value.replace(":", "x")}-${ad.id}`}
                      >
                        Copy &amp; reframe to {r.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {onFixBands && currentRatio === "9:16" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onFixBands(ad)}
                  disabled={isFixingBands || !url}
                  className="h-8 text-xs col-span-2"
                  data-testid={`button-fix-bands-${ad.id}`}
                >
                  {isFixingBands ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Fixing bands…
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-3.5 w-3.5 mr-1" />
                      Fix bands
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
});
function EmptyState({
  status,
  hasFilters,
  brandId,
}: {
  status: LibraryStatus;
  hasFilters: boolean;
  brandId: string;
}) {
  if (hasFilters) {
    return (
      <div className="text-center py-16 text-sm text-muted-foreground" data-testid="empty-filtered">
        No ads match those filters.
      </div>
    );
  }
  if (status === "archived") {
    return (
      <div className="text-center py-16 text-sm text-muted-foreground" data-testid="empty-archived">
        {ARCHIVE_EMPTY_COPY}
      </div>
    );
  }
  return (
    <div className="text-center py-16 space-y-3" data-testid="empty-default">
      <Bookmark className="h-8 w-8 mx-auto text-muted-foreground" />
      <div>
        <p className="text-sm font-medium">No saved ads yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Generate ads in Ads Express, then click <span className="font-medium">Save</span> to keep them here.
        </p>
      </div>
      <Link href={`/brands/${brandId}/ads-express`}>
        <Button size="sm" className="mt-2" data-testid="button-go-to-ads-express">
          <ExternalLink className="h-4 w-4 mr-1.5" />
          Open Ads Express
        </Button>
      </Link>
    </div>
  );
}

const VIEW_MORE_BATCH = 24;

function flattenPages(data: InfiniteData<LibraryPage> | undefined): LibraryAd[] {
  // Tolerate malformed cached pages (missing/undefined items) instead of
  // crashing the whole page — treat them as empty (Task #1756).
  return data ? data.pages.flatMap((p) => (Array.isArray(p?.items) ? p.items : [])) : [];
}

function pagesTotal(data: InfiniteData<LibraryPage> | undefined): number {
  if (!data || data.pages.length === 0) return 0;
  const total = data.pages[data.pages.length - 1]?.total;
  return typeof total === "number" && Number.isFinite(total) ? total : 0;
}

type LibraryPage = {
  items: LibraryAd[];
  total: number;
  limit: number;
  offset: number;
};

// Task #1756 — only `{ items: [], total }`-shaped pages may enter the
// infinite-query cache. Normalizes the two malformed shapes we can receive:
// a legacy ARRAY body (a server that ignored `paged=1`, e.g. deploy skew
// mid-rollout) becomes a single terminal page, and anything else non-paged
// (error/HTML body parsed as JSON, null, etc.) becomes an empty terminal
// page rather than a cache entry that crashes getNextPageParam/flattenPages.
function normalizeLibraryPage(raw: unknown, offset: number): LibraryPage {
  if (Array.isArray(raw)) {
    return { items: raw as LibraryAd[], total: raw.length, limit: LIBRARY_PAGE_SIZE, offset };
  }
  const obj = raw as Partial<LibraryPage> | null | undefined;
  if (obj && Array.isArray(obj.items)) {
    return {
      items: obj.items,
      total: typeof obj.total === "number" && Number.isFinite(obj.total) ? obj.total : obj.items.length,
      limit: typeof obj.limit === "number" ? obj.limit : LIBRARY_PAGE_SIZE,
      offset: typeof obj.offset === "number" ? obj.offset : offset,
    };
  }
  return { items: [], total: 0, limit: LIBRARY_PAGE_SIZE, offset };
}

function useLibraryCategoryQuery(
  listUrl: string,
  brandId: string | undefined,
  category: LibraryCategory,
): CategoryQuery {
  return useInfiniteQuery<LibraryPage, Error, InfiniteData<LibraryPage>, [string, string, LibraryCategory], number>({
    queryKey: [listUrl, "paged", category],
    queryFn: async ({ pageParam }) => {
      const res = await fetch(
        `${listUrl}&category=${category}&paged=1&limit=${LIBRARY_PAGE_SIZE}&offset=${pageParam ?? 0}`,
        { credentials: "include" },
      );
      // Mirror the old getQueryFn({ on401: "returnNull" }) behavior: an
      // expired session renders an empty library instead of an error wall.
      if (res.status === 401) return { items: [], total: 0, limit: LIBRARY_PAGE_SIZE, offset: 0 };
      if (!res.ok) throw new Error(`Failed to load ads (${res.status})`);
      // A non-JSON body (e.g. an HTML error page served with 200) must not
      // enter the cache as a malformed page — surface it as a query error.
      let raw: unknown;
      try {
        raw = await res.json();
      } catch {
        throw new Error("Failed to load ads (invalid response)");
      }
      return normalizeLibraryPage(raw, pageParam ?? 0);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      // Defensive against malformed cached pages — never throw here (a throw
      // takes down the whole page with the runtime-error overlay).
      const loaded = pages.reduce(
        (n, p) => n + (Array.isArray(p?.items) ? p.items.length : 0),
        0,
      );
      const lastItems = Array.isArray(lastPage?.items) ? lastPage.items : [];
      const total = typeof lastPage?.total === "number" ? lastPage.total : 0;
      // Stop when everything is loaded, or when the server returned an empty
      // page (total may have shrunk between requests).
      if (lastItems.length === 0) return undefined;
      return loaded < total ? loaded : undefined;
    },
    enabled: !!brandId,
  });
}
